/**
 * src/UtilitiesPage.js
 *
 * VISINVOKE Utilities - active and planned utilities showcase.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useContentPlannerLaunch } from './config/contentPlannerLaunch';
import API from './api';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { AvailabilityBadge, FreeBadge } from './components/productPrimitives';
import { hexA } from './RegistryComponents';
import { PRODUCTS } from './config/products';
import { openDataStatusLabel, useOpenDataReleaseState } from './config/openDataRelease';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { SCC_CLOUD_OPTIONS, SCC_WORKFORCE_EXTENSIONS } from './config/sccPricing';
import {
  REGISTRY_CATEGORIES,
  REGISTRY_BASE_ROUTE,
  REGISTRY_DEFAULTS,
} from './registryConfig';

// In-page section navigation. IDs are page anchors ONLY (not application slugs,
// product IDs, route names, or tracking identifiers).
const UTIL_SECTION_LINKS = [
  { id: 'paid-utilities',      label: 'Paid Utilities' },
  { id: 'utility-collections', label: 'Utility Collections' },
];

// scroll-margin-top so an anchored heading clears the sticky ProductNav header
// (utility bar minHeight 50 + main bar minHeight 104, plus a little buffer).
const UTIL_ANCHOR_OFFSET = 170;

const UTIL_MOTION_CSS = `
  .util-reveal {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 420ms cubic-bezier(0.22,1,0.36,1),
                transform 420ms cubic-bezier(0.22,1,0.36,1);
  }
  .util-reveal.util-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .util-card-hover {
    transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 220ms ease,
                border-color 220ms ease !important;
  }
  .util-card-hover:hover {
    transform: translateY(-4px) !important;
    box-shadow: 0 14px 40px rgba(0,0,0,0.32) !important;
    border-color: rgba(100,116,139,0.7) !important;
  }
  .util-btn-hover {
    transition: transform 160ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 180ms ease,
                filter 180ms ease !important;
  }
  .util-btn-hover:hover {
    filter: brightness(1.12) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.25) !important;
  }
  .util-btn-hover:active {
    transform: translateY(1px) !important;
    filter: brightness(0.92) !important;
  }
  .wi-columns {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 28px;
  }
  .wi-badges {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }
  @media (max-width: 900px) {
    .wi-badges {
      display: flex;
      flex-wrap: wrap;
    }
  }
  @media (max-width: 1200px) {
    .wi-columns {
      grid-template-columns: repeat(2, 1fr);
      gap: 24px 28px;
    }
  }
  @media (max-width: 640px) {
    .wi-columns {
      grid-template-columns: 1fr;
      gap: 22px;
    }
  }
  .wi-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
    align-items: stretch;
  }
  @media (max-width: 640px) {
    .wi-cards {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }
  /* SCC Cloud Services — three equal columns on desktop, two at intermediate
     widths, one on narrow/mobile. Columns live only here (not inline) so the
     breakpoints are never overridden by an inline grid-template-columns. */
  .cs-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    align-items: stretch;
  }
  @media (max-width: 1024px) {
    .cs-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 18px;
    }
  }
  @media (max-width: 640px) {
    .cs-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }
  /* Workforce Extensions — four/two/one column behaviour, tighter gaps. */
  .wx-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
    align-items: stretch;
  }
  @media (max-width: 1024px) {
    .wx-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
  }
  @media (max-width: 640px) {
    .wx-grid {
      grid-template-columns: 1fr;
      gap: 14px;
    }
  }
  /* MAIN PROGRAMS row — the established main-product card system. Each card
     keeps the SAME width/proportion it had when two cards shared the row
     (flex-basis grows to the established ~620px card width), so a single main
     product is never stretched across the whole content width. The row centres
     itself, so removing a card never leaves a broken-looking empty column. */
  .mp-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
  .mp-row > * {
    box-sizing: border-box;
    flex: 1 1 340px;
    min-width: 0;
  }
  /* A main-product card fills the Main Programs width. Its internal layout
     splits into identity/pricing/action and capability columns so the wider
     card reads as designed for the space rather than stretched. */
  .pp-split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 26px 44px;
    align-items: start;
  }
  @media (max-width: 920px) {
    .pp-split {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }
  /* CONTENT PLANNER capability strip — five per row on desktop, folding to a
     wrapped strip on narrow widths. Columns live only here so the breakpoints
     are never overridden by an inline grid-template-columns. */
  .cp-badges {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
  @media (max-width: 1200px) {
    .cp-badges {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  @media (max-width: 760px) {
    .cp-badges {
      display: flex;
      flex-wrap: wrap;
    }
  }
  /* CONTENT PLANNER tiers — two capacity tier cards side by side beneath the
     main Content Planner product block, stacking on narrow widths. */
  .cp-tiers {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    align-items: stretch;
  }
  @media (max-width: 860px) {
    .cp-tiers {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }
  /* Paid Utilities collection card — the page's primary card. Identity and
     purpose on the left, the catalogue contents and CTA on the right, so the
     full-width card reads as designed for the space. Stacks on narrow widths. */
  .pu-hero {
    display: flex;
    flex-direction: column;
    gap: 22px;
    align-items: stretch;
  }
  /* IN THIS CATALOGUE - fewer, larger paid-utility cards. Three wide on desktop,
     two on laptop, one on phone, so each product has real horizontal room and the
     internal rows (title / type / price / description / status) line up across a row. */
  .uc-paid-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    align-items: stretch;
  }
  @media (max-width: 1240px) { .uc-paid-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 680px)  { .uc-paid-grid { grid-template-columns: 1fr; } }
  .uc-paid-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  @media (max-width: 680px) { .uc-paid-name { white-space: normal; } }
  .uc-paid-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .uc-paid-card { transition: transform 160ms ease, border-color 160ms ease, background 160ms ease; }
  .uc-paid-card:hover { transform: translateY(-3px); border-color: rgba(255,255,255,0.28); background: rgba(255,255,255,0.08); }
  /* Utility Collections — two large destination cards side by side on desktop,
     stacking to one column on narrow widths. Columns live only here (not inline)
     so the breakpoint is never overridden by an inline grid-template-columns. */
  .uc-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    align-items: stretch;
  }
  @media (max-width: 860px) {
    .uc-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
`;

// Retained product data. The full Service Command Centre presentation no longer
// renders on this landing page (the catalogue card on the Paid Utilities page
// covers it), so this and the detailed section components below are exported
// rather than deleted: they remain available for a future product detail page.
export const ACTIVE_UTILITIES = [
  {
    icon: '🔧',
    paid: true,
    kind: 'scc',
    eyebrow: 'SERVICE COMMAND CENTRE',
    title: 'Service Command Centre',
    accent: '#38bdf8',
    price: '$65 USD',
    billing: 'Per Month',
    body: 'Run an entire service business from a single operational workspace for bookings, customers, devices, jobs, invoices, payments, communications, inventory, suppliers and business operations.',
    features: [
      'Booking Management',
      'Customer Management',
      'Device Tracking',
      'Job Workflows',
      'Communications',
      'Payments & Invoicing',
      'Inventory Control',
      'Operational Reporting',
    ],
    // Real launch path. Only used when the signed-in user holds an active
    // service_command_centre_access entitlement (resolved from the real user
    // object via isEntitlementActive). Not used while the product is in the
    // non-active/subscription-required state. Matches the real app route
    // registered in App.js: /dashboard/utilities/service-command-centre.
    launchUrl: 'https://app.visinvoke.com/dashboard/utilities/service-command-centre',
  },
];

// ---------------------------------------------------------------------------
// SCC CLOUD SERVICES — optional VISINVOKE-managed backup / retention / recovery.
// These tiers are OPTIONAL managed protection layered on top of the customer's
// own storage. They do NOT unlock SCC functionality and are NOT feature-unlock
// editions. Presentation only: the approved dashboard pricing display. No
// purchase and plan-change wiring is server-authoritative; this section remains
// presentation-only because account billing is where an active plan is changed.
// The middle tier (Guardian) is the recommended tier and carries a restrained
// gold/accent treatment consistent with the dashboard's existing amber accent.
// ---------------------------------------------------------------------------
const SCC_CLOUD_ACCENT = '#38bdf8'; // dashboard SCC blue
const SCC_CLOUD_GOLD   = '#f59e0b'; // dashboard amber/gold accent (recommended)

export const SCC_CLOUD_TIERS = SCC_CLOUD_OPTIONS.map((tier) => ({
  ...tier,
  features: tier.items,
}));

// WORKFORCE EXTENSIONS — expand the staff allowance beyond the 10-staff base.
// These are NOT separate SCC editions; they only raise the total staff allowance
// and never unlock functionality. Figures below are TOTAL staff allowances.
// Total-staff figures are emphasised with the dashboard's blue accent. Approved,
// fixed pricing. Changes are made from Account > Manage and reconciled by the
// Stripe webhook; the higher tiers never unlock product features.
export const WORKFORCE_EXTENSIONS = SCC_WORKFORCE_EXTENSIONS;

// ---------------------------------------------------------------------------
// VISINVOKE CONTENT PLANNER — standalone PAID subscription utility.
// Presented directly below the Website Capture Suite section using the same
// structural pattern: one main product presentation followed by its pricing /
// capacity tier cards.
//
// Every value below comes from the current authoritative Content Planner
// project:
//   D:\...\visinvoke-desktop-app\src\contentplanner\plans.js  (prices, capacity,
//     shared feature set)
//   ...\docs\CONTENT_PLANNER_INITIAL_BUILD.md                 (product record)
// Both tiers reference the SAME complete feature set. Media+ changes resource
// allowances only - no capability is withheld from Standard. No checkout seam
// exists for Content Planner yet, so the action stays Coming Soon (matching the
// Website Capture Suite treatment); nothing here grants or implies access.
// ---------------------------------------------------------------------------
const CONTENT_PLANNER_ACCENT = '#f472b6';

// The complete Content Planner capability set (CONTENT_PLANNER_FEATURES in
// plans.js), in readable form. Identical for BOTH tiers.
const CONTENT_PLANNER_FEATURES = [
  'Bulk Import',
  'DOCX Import',
  'Spreadsheet Import',
  'Content Library',
  'Bulk Editing',
  'Review Workflow',
  'Campaigns',
  'Calendar',
  'Saved Schedules',
  'Evergreen Content',
  'Analytics',
  'Media Rotation',
  'Private Metadata Removal',
  'PC-Off Publishing',
  'All Supported Platforms',
];

// Two capacity tiers. Same feature set, different cloud/media resources.
const CONTENT_PLANNER_TIERS = [
  {
    eyebrow: 'COMPLETE CONTENT PLANNER',
    title: 'Content Planner Standard',
    price: '$5.99',
    billing: '/mo',
    capacity: '10 GB cloud media',
    features: [
      'Complete Content Planner feature set',
      'Around 10 GB cloud media capacity',
      '1 GB source planning ceiling per file',
      'Cloud-backed scheduled publishing',
    ],
  },
  {
    eyebrow: 'COMPLETE CONTENT PLANNER — MORE CLOUD MEDIA',
    title: 'Content Planner Media+',
    price: '$8.99',
    billing: '/mo',
    capacity: '100 GB cloud media',
    features: [
      'Complete Content Planner feature set',
      'Around 100 GB cloud media capacity',
      '5 GB source planning ceiling per file',
      'Cloud-backed scheduled publishing',
    ],
  },
];

// ---------------------------------------------------------------------------
// PAID VISINVOKE UTILITIES — the commercial utility catalogue.
// Source of truth for the dedicated Paid Utilities page (PaidUtilitiesPage.js),
// mirroring how FREE_UTILITIES backs the Free Utilities page.
//
// Ordered by primary monthly price, highest first. Content Planner stays last
// because it is the lower-priced product family; its Media+ tier does not
// reorder the catalogue.
//
// Every value is taken from the presentations already approved on this page
// (Service Command Centre / Website Capture Suite / Content Planner) or derived
// from the arrays above, so add-on price ranges cannot drift from the tiers
// themselves. Nothing new is invented here.
// ---------------------------------------------------------------------------
const SCC_ADDON_NOTE =
  'Optional SCC Cloud Services (' + SCC_CLOUD_TIERS[0].price + '–' + SCC_CLOUD_TIERS[SCC_CLOUD_TIERS.length - 1].price + '/mo) and ' +
  'Workforce Extensions (' + WORKFORCE_EXTENSIONS[0].price + '–' + WORKFORCE_EXTENSIONS[WORKFORCE_EXTENSIONS.length - 1].price.replace('+', '') + '/mo) are available. ' +
  'They add managed backup or staff capacity and are never required to use SCC.';

export const PAID_UTILITIES = [
  {
    key: 'scc',
    kind: 'scc',
    detailKey: 'service-command-centre',
    icon: '🔧',
    accent: '#38bdf8',
    eyebrow: 'OPERATIONAL BUSINESS PLATFORM',
    title: 'VISINVOKE Service Command Centre',
    price: '$65 USD',
    billing: 'Per Month',
    body: 'Run an entire service business from a single operational workspace for bookings, customers, devices, jobs, invoices, payments, communications, inventory, suppliers and business operations.',
    features: [
      'Booking Management',
      'Customer Management',
      'Device Tracking',
      'Job Workflows',
      'Communications',
      'Payments & Invoicing',
      'Inventory Control',
      'Operational Reporting',
    ],
    access: 'Opens in your VISINVOKE dashboard',
    note: SCC_ADDON_NOTE,
  },
  {
    key: 'backup-verification-cloud',
    detailKey: 'backup-verification-cloud',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: '☁️',
    accent: '#60a5fa',
    eyebrow: 'IT OPERATIONS • BACKUP, RECOVERY AND RESILIENCE',
    title: 'Backup Verification Cloud',
    headline: 'Know your backups can recover the business.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$59.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$249.99 USD', capacity: 'per month / company' },
    ],
    body: 'Backup Verification Cloud restores selected recovery points into approved test locations, validates the recovered content and keeps clear evidence of what passed, what failed and whether recovery objectives were met. Heavy verification runs on your approved computer or runner while VISINVOKE Cloud coordinates schedules, alerts and shared history.',
    features: [
      'Perform controlled restore rehearsals instead of trusting job status alone.',
      'Validate files, archives, databases, disks and supported application runbooks.',
      'Measure RPO and RTO outcomes and expose stale protection.',
      'Keep signed evidence, exceptions, cleanup records and audit-ready reports.',
      'Coordinate multiple providers and local runners without sending backup payloads to VISINVOKE Cloud.',
    ],
    demoPresentation: 'Explore a permanent sample workspace. A bounded live demo can verify a small set of your own backups when live-demo access is enabled.',
    localFirst: 'Restored backup payloads stay in your controlled environment.',
    dccWording: 'Runs in a dedicated desktop window through VISINVOKE DCC.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Backup Verification Cloud',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'contractor-compliance-vault',
    detailKey: 'contractor-compliance-vault',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'CCV',
    accent: '#2dd4bf',
    eyebrow: 'COMPLIANCE AND WORKFORCE OPERATIONS',
    title: 'Contractor Compliance Vault',
    headline: 'Contractor readiness you can explain and prove.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$59.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$249.99 USD', capacity: 'per month / company' },
    ],
    body: 'Contractor Compliance Vault collects company, worker, vehicle and plant evidence through a secure portal, applies your versioned requirements and shows who is ready for each job, site and date. Local document processing keeps heavy work on your computer while VISINVOKE Cloud powers shared review, expiry monitoring and durable audit history.',
    features: [
      'Versioned requirements.',
      'Secure evidence.',
      'Review, renew, waive and suspend trail.',
      'Mobilisation readiness.',
      'Privacy-controlled point-in-time export.',
    ],
    demoPresentation: 'Permanent sample programme plus bounded live onboarding when enabled.',
    localFirst: 'OCR, preparation, bulk checks and large exports run locally where practical.',
    dccWording: 'Runs in a dedicated desktop window through VISINVOKE DCC.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Contractor Compliance Vault',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'credential-renewal-manager',
    detailKey: 'credential-renewal-manager',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'CRM',
    accent: '#a78bfa',
    eyebrow: 'COMPLIANCE AND WORKFORCE OPERATIONS',
    title: 'Credential & Renewal Manager',
    headline: 'Renew every credential before it becomes a blocker.',
    price: '$19.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$19.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$39.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$79.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$159.99 USD', capacity: 'per month / company' },
    ],
    body: 'Credential & Renewal Manager keeps licences, registrations, certifications, permits, memberships and other credentials in one verified lifecycle. Plan prerequisites, collect evidence securely, monitor issuer status and prove what was current at any date. Heavy document processing runs locally while VISINVOKE Cloud powers shared work, reminders and audit history.',
    features: [
      'Typed credential register.',
      'Explicit verification.',
      'Prerequisite-aware renewal pipeline.',
      'Future readiness.',
      'Private signed audit exports.',
    ],
    demoPresentation: 'Permanent sample portfolio plus bounded live credential trial when enabled.',
    localFirst: 'Heavy document processing runs locally while shared work, reminders and audit history use VISINVOKE Cloud.',
    dccWording: 'Runs in a dedicated desktop window through VISINVOKE DCC with local-first document processing.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Credential & Renewal Manager',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'compliance-evidence-vault',
    detailKey: 'compliance-evidence-vault',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'CEV',
    accent: '#2dd4bf',
    eyebrow: 'GOVERNANCE, RISK AND COMPLIANCE',
    title: 'Compliance Evidence Vault',
    headline: 'Evidence that stays connected to what it proves.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$59.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$249.99 USD', capacity: 'per month / company' },
    ],
    body: 'Compliance Evidence Vault captures files and system records with provenance, maps them to versioned controls, monitors evidence gaps and creates tightly scoped audit rooms. Heavy hashing, OCR, redaction and package work runs locally while VISINVOKE Cloud provides encrypted sharing, recurring collection and durable history.',
    features: [
      'Provenance and integrity.',
      'Scoped control mappings.',
      'Recurring requests.',
      'Privacy-limited audit rooms.',
      'Retention, hold and disposition trail.',
    ],
    demoPresentation: 'Permanent sample programme plus bounded live evidence workspace when enabled.',
    localFirst: 'Heavy hashing, OCR, redaction and package work runs locally where practical.',
    dccWording: 'Runs in a dedicated desktop window through VISINVOKE DCC with local-first evidence processing.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Compliance Evidence Vault',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'maintenance-and-inspection-cloud',
    detailKey: 'maintenance-and-inspection-cloud',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'MIC',
    accent: '#f59e0b',
    eyebrow: 'ASSETS, MAINTENANCE AND FIELD OPERATIONS',
    title: 'Maintenance & Inspection Cloud',
    headline: 'Every asset, inspection and repair in one controlled history.',
    price: '$19.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$19.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$39.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$79.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$159.99 USD', capacity: 'per month / company' },
    ],
    body: 'Maintenance & Inspection Cloud connects assets, maintenance plans, inspections, defects, work orders, parts, field evidence and return-to-service decisions. Local processing keeps large media and analytics on your computer while VISINVOKE Cloud coordinates schedules, offline teams, portals and shared history.',
    features: [
      'Asset hierarchy.',
      'Maintenance strategies.',
      'Typed inspections.',
      'Safe defect and return-to-service controls.',
      'Offline field work.',
      'Reliability and cost reporting.',
    ],
    demoPresentation: 'Permanent sample estate plus bounded live maintenance trial.',
    localFirst: 'Local processing keeps large media and analytics on your computer while VISINVOKE Cloud coordinates shared operations.',
    dccWording: 'Dedicated DCC window and local-first heavy processing.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Maintenance & Inspection Cloud',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'secure-client-document-portal',
    detailKey: 'secure-client-document-portal',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'SCDP',
    accent: '#38bdf8',
    eyebrow: 'SECURE COLLABORATION AND CLIENT SERVICES',
    title: 'Secure Client Document Portal',
    headline: 'A safer, clearer way to exchange client documents.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$59.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$249.99 USD', capacity: 'per month / company' },
    ],
    body: 'Secure Client Document Portal replaces sensitive email attachments with structured requests, resumable uploads, quarantine, review, replacement and final delivery. Heavy preparation, redaction and packaging runs locally while VISINVOKE Cloud provides encrypted storage, client access, reminders and audit history.',
    features: [
      'Itemised requests.',
      'Safe upload and quarantine.',
      'Versioned review and replacement.',
      'Controlled final delivery.',
      'Access and retention audit.',
    ],
    demoPresentation: 'Permanent sample portal plus bounded live client exchange when enabled.',
    localFirst: 'Heavy preparation, redaction and packaging runs locally while VISINVOKE Cloud provides encrypted collaboration.',
    dccWording: 'Dedicated DCC workspace with local-first document processing.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Secure Client Document Portal',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'website-dns-ssl-uptime-monitor',
    detailKey: 'website-dns-ssl-uptime-monitor',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'WDSUM',
    accent: '#22d3ee',
    eyebrow: 'WEBSITE AND INTERNET OPERATIONS',
    title: 'Website / DNS / SSL / Uptime Monitor',
    headline: 'Know when the website, DNS or certificate really fails.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: 'per month / company' },
      { title: 'FORGE', price: '$59.99 USD', capacity: 'per month / company' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: 'per month / company' },
      { title: 'APEX', price: '$249.99 USD', capacity: 'per month / company' },
    ],
    body: 'Website / DNS / SSL / Uptime Monitor checks availability, content, redirects, DNS, DNSSEC and certificate chains from independent regions, confirms incidents before escalating and records the recovery evidence. Private probes compare internal origins while VISINVOKE Cloud keeps monitoring when your computers are off.',
    features: [
      'Regional quorum.',
      'HTTP assertions.',
      'DNS and DNSSEC.',
      'TLS and certificate health.',
      'Correlated incidents.',
      'Privacy-safe status pages.',
    ],
    demoPresentation: 'Permanent sample monitoring plus bounded live target trial.',
    localFirst: 'Private probes compare approved internal origins while VISINVOKE Cloud keeps authorised monitoring active when company computers are off.',
    dccWording: 'Dedicated DCC control centre with private local probes.',
    access: 'Dedicated desktop window through VISINVOKE DCC',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'Explore Website Monitoring',
    typeLabel: 'Paid VISINVOKE Utility',
  },
  {
    key: 'local-seo-grid-rank-monitor',
    detailKey: 'local-seo-grid-rank-monitor',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'LSGRM',
    accent: '#8f5dff',
    eyebrow: 'LOCAL MARKETING INTELLIGENCE',
    title: 'Local SEO Grid Rank Monitor',
    headline: 'See where local visibility changes across the service area.',
    price: '$29.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Plans based on locations and observations, not users',
    tiers: [
      { title: 'INVOKE', price: '$29.99 USD', capacity: '5 locations · 50 pairs · 10,000 observations' },
      { title: 'FORGE', price: '$59.99 USD', capacity: '25 locations · 500 pairs · 100,000 observations' },
      { title: 'NEXUS', price: '$129.99 USD', capacity: '150 locations · 5,000 pairs · 1,000,000 observations' },
      { title: 'APEX', price: '$249.99 USD', capacity: '1,000 locations · 30,000 pairs · 8,000,000 observations' },
    ],
    body: 'Local SEO Grid Rank Monitor measures location-specific local search visibility with comparable grids, evidence and responsible action planning. It preserves missing and ambiguous outcomes, provider-policy provenance and privacy-safe service-area context without promising ranking improvement.',
    features: [
      'Versioned location, query and grid context.',
      'Seven explicit observation outcomes.',
      'Comparable spatial movement evidence.',
      'Provider-policy and attribution controls.',
      'Responsible actions and client reports.',
      'Capacity, cost and immutable audit evidence.',
    ],
    demoPresentation: 'Permanent signed fictional sample plus a bounded seven-day local synthetic demo with no live provider, email, webhook or Stripe charge.',
    localFirst: 'Local planning, spatial review and privacy-safe preparation combine with authorised Cloud observation, scheduling and collaboration boundaries.',
    dccWording: 'Dedicated DCC spatial visibility control centre with an accessible point-table alternative.',
    access: 'Planned-capability details only; no launch, purchase, checkout or subscription path',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'View planned capabilities',
    typeLabel: 'Paid VISINVOKE Utility · VPU-100-008 · Not Released',
  },
  {
    key: 'equipment-service-and-calibration-manager',
    detailKey: 'equipment-service-and-calibration-manager',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'ESCM',
    accent: '#35b7a8',
    eyebrow: 'EQUIPMENT ASSURANCE',
    title: 'Equipment Service & Calibration Manager',
    headline: 'Know which equipment is fit for use and why.',
    price: '$19.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Plans based on equipment and completed activities · unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$19.99 USD', capacity: '250 active equipment · 100 completed activities/month · 2 managed customers · 5 sites · 25 programmes · 50 reference standards · 10 automation rules · 10 share grants · 24-month full-resolution history · 10 GB · 1,000 emails/month · 2 provider connections' },
      { title: 'FORGE', price: '$39.99 USD', capacity: '2,000 active equipment · 1,000 completed activities/month · 20 managed customers · 50 sites · 200 programmes · 500 reference standards · 50 automation rules · 100 share grants · 48-month full-resolution history · 100 GB · 10,000 emails/month · 10 provider connections' },
      { title: 'NEXUS', price: '$79.99 USD', capacity: '20,000 active equipment · 10,000 completed activities/month · 200 managed customers · 500 sites · 2,000 programmes · 5,000 reference standards · 250 automation rules · 1,000 share grants · 84-month full-resolution history · 1 TB · 100,000 emails/month · 50 provider connections' },
      { title: 'APEX', price: '$159.99 USD', capacity: '250,000 active equipment · 100,000 completed activities/month · 2,000 managed customers · 5,000 sites · 20,000 programmes · 50,000 reference standards · 1,000 automation rules · 10,000 share grants · 120-month full-resolution history · 10 TB · 500,000 emails/month · 250 provider connections' },
    ],
    body: 'Equipment Service & Calibration Manager controls equipment status, service, calibration and verification evidence, certificate review, intended-use fitness and out-of-tolerance impact. It is not a calibration laboratory, accreditation body, regulator, universal CMMS or substitute for competent technical judgement.',
    features: [
      'Controlled equipment, programme and due-state records.',
      'Distinct service, calibration and verification workflows.',
      'As-found, as-left, uncertainty and decision-rule evidence.',
      'Certificate technical review and visible reference-chain gaps.',
      'Out-of-tolerance containment, impact and return-to-service controls.',
      'Local and offline field work with bounded Cloud coordination.',
    ],
    demoPresentation: 'Permanent signed fictional sample plus a bounded seven-day local synthetic demo. Every demo record is invalid for equipment use; no provider, email, share or Stripe charge is enabled.',
    localFirst: 'Local equipment, field and measurement workflows preserve signed evidence offline; optional Cloud coordination remains tenant-, role-, competence-, entitlement- and freshness-bound.',
    dccWording: 'Dedicated DCC equipment-assurance control centre with accessible evidence tables and a paired traceability chain table.',
    access: 'Planned-capability details only; no launch, purchase, checkout, subscription, accreditation or certification path',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'View planned capabilities',
    typeLabel: 'Paid VISINVOKE Utility · VPU-100-009 · Not Released',
  },
  {
    key: 'fleet-service-and-inspection-manager',
    detailKey: 'fleet-service-and-inspection-manager',
    status: 'Coming Soon',
    kind: 'information-only',
    icon: 'FSIM',
    accent: '#2f9f86',
    eyebrow: 'FLEET ROADWORTHINESS',
    title: 'Fleet Service & Inspection Manager',
    headline: 'Know what is safe to dispatch and what still needs action.',
    price: '$19.99 USD',
    billing: 'Per Month / Company',
    priceNote: 'Plans based on fleet units and completed events · unlimited authorised company users',
    tiers: [
      { title: 'INVOKE', price: '$19.99 USD', capacity: '50 active fleet units · 2,000 completed checks/inspection/service events monthly · 5 depots · 2 managed customers · 25 programmes/templates · 2 providers · 10 automation rules · 10 active shares · 10 GB · 24-month full-resolution history · 1,000 emails/month' },
      { title: 'FORGE', price: '$39.99 USD', capacity: '500 active fleet units · 25,000 completed checks/inspection/service events monthly · 50 depots · 20 managed customers · 200 programmes/templates · 10 providers · 50 automation rules · 100 active shares · 100 GB · 48-month full-resolution history · 10,000 emails/month' },
      { title: 'NEXUS', price: '$79.99 USD', capacity: '5,000 active fleet units · 300,000 completed checks/inspection/service events monthly · 500 depots · 200 managed customers · 2,000 programmes/templates · 50 providers · 250 automation rules · 1,000 active shares · 1 TB · 84-month full-resolution history · 100,000 emails/month' },
      { title: 'APEX', price: '$159.99 USD', capacity: '100,000 active fleet units · 5,000,000 completed checks/inspection/service events monthly · 5,000 depots · 2,000 managed customers · 20,000 programmes/templates · 250 providers · 1,000 automation rules · 10,000 active shares · 10 TB · 120-month full-resolution history · 500,000 emails/month' },
    ],
    body: 'Fleet Service & Inspection Manager connects daily observations, defects, service, skilled inspections, repairs and competent clearance to dispatch decisions for vehicle, trailer and powered-attachment fleets. It does not provide regulator approval, legal roadworthiness, guaranteed compliance or a substitute for competent judgement, and it is not a worker-disciplinary surveillance system.',
    features: [
      'Vehicle, trailer and powered-attachment register.',
      'Distinct daily-check and skilled-inspection evidence.',
      'Due programmes, schedules and meter provenance.',
      'Defect, workshop, repair and competent-clearance controls.',
      'Dispatch snapshots, combinations and campaign evidence.',
      'Local and offline field work with bounded Cloud coordination.',
    ],
    demoPresentation: 'Permanent signed fictional sample plus a bounded seven-day local synthetic demo. Demo evidence is not proof of legal roadworthiness; no provider, email, share, dispatch, charge or production inspection side effect is enabled.',
    localFirst: 'Local and offline fleet work preserves signed evidence at the point of use; optional Cloud coordination remains tenant-, role-, competence-, entitlement-, provider-authority- and freshness-bound.',
    dccWording: 'Dedicated three-pane fleet control centre with accessible table and list alternatives.',
    access: 'Planned-capability details only; no launch, purchase, checkout, subscription, regulator approval or legal-roadworthiness path',
    statusLabel: 'Coming Soon',
    dueLabel: 'Due 2027',
    ctaLabel: 'View planned capabilities',
    typeLabel: 'Paid VISINVOKE Utility · VPU-100-010 · Not Released',
  },
  {
    key: 'website-capture',
    kind: 'coming-soon',
    // Availability is decided by the SERVER launch gate
    // (GET /v1/website-capture/commerce), never by this catalogue entry. The
    // price stays visible because VISINVOKE presents what is coming; the
    // purchase path stays closed until sales are explicitly enabled at launch.
    launchGate: 'website-capture',
    detailKey: 'website-capture-suite',
    icon: '🌐',
    accent: '#22d3ee',
    eyebrow: 'PROFESSIONAL WEBSITE INTELLIGENCE PLATFORM',
    title: 'VISINVOKE Website Capture Suite',
    price: '$14.99 USD',
    billing: 'Per Month',
    priceNote: 'Unlimited projects · ongoing updates',
    body: 'Automatically discover, crawl, analyse and document websites into a structured database ready for auditing, migration, compliance, AI processing, documentation and long-term website management.',
    features: [
      'Unlimited Projects',
      'Unlimited Websites',
      'Recursive Crawling',
      'Browser Rendering',
      'JavaScript Support',
      'Crawl History',
      'Crawl Validation',
      'AI Ready',
    ],
    access: 'Runs via DCC · available to buy at VISINVOKE launch',
  },
  {
    key: 'content-planner',
    kind: 'coming-soon',
    // Availability is decided by the SERVER launch gate
    // (GET /v1/content-planner/commerce), never by this catalogue entry. The
    // tiers and intended prices stay visible because VISINVOKE presents what is
    // coming; the purchase path stays closed until sales are explicitly enabled.
    launchGate: 'content-planner',
    detailKey: 'content-planner',
    icon: '📅',
    accent: CONTENT_PLANNER_ACCENT,
    eyebrow: 'CONTENT PLANNING & SCHEDULING',
    title: 'VISINVOKE Content Planner',
    price: '$5.99 – $8.99 USD',
    billing: 'Per Month',
    priceNote: 'Two capacity tiers · identical feature set',
    tiers: CONTENT_PLANNER_TIERS.map(t => ({ title: t.title, price: t.price, capacity: t.capacity })),
    body: 'Plan, schedule and publish business content from one workspace: bulk import, a reusable content library, review and approval, campaigns, a scheduling calendar, media rotation and analytics. Scheduled publishing is cloud-backed so it continues when your PC and DCC are shut down.',
    features: [
      'Bulk Import',
      'Content Library',
      'Review Workflow',
      'Campaigns',
      'Calendar',
      'Media Rotation',
      'Private Metadata Removal',
      'PC-Off Publishing',
    ],
    featureNote: 'All ' + CONTENT_PLANNER_FEATURES.length + ' capabilities are identical on both tiers.',
    access: 'Runs via DCC',
  },
  {
    key: 'image-toolkit-ai-rebuild',
    kind: 'coming-soon',
    moreInfoRoute: '/dashboard/utilities/ai-rebuild',
    icon: '\u2601',
    accent: '#34d399',
    eyebrow: 'PAID CLOUD ADD-ON FOR THE FREE IMAGE TOOLKIT',
    title: 'VISINVOKE Image Toolkit AI Rebuild',
    price: 'Creation Credits',
    billing: 'Pay per rebuild',
    priceNote: 'Exact credit cost shown before you submit · pricing being finalised',
    body: 'A paid cloud add-on for the free VISINVOKE Image Toolkit. When an existing image is genuinely too small or too low in detail for what you need (a 4K screen, a large print, an old or small asset), AI Rebuild reconstructs it at substantially higher detail, using your existing image as the authoritative visual reference. It is not a new picture and not a prompt-to-image generator; the rest of Image Toolkit stays free and local.',
    features: [
      'For low-resolution or inadequate originals',
      'Old or small digital assets; artwork that cannot be enlarged cleanly',
      'Targets for 4K / high-resolution display and larger print sizes',
      'When ordinary Resize or free local Enhance is not enough',
      'Your image is the reference, reconstructed rather than replaced',
      'Charged in VISINVOKE Creation Credits, shown before you submit',
      'Result returns into Image Toolkit to finish locally',
    ],
    access: 'Extends the free Image Toolkit · runs via the VISINVOKE cloud',
    note: 'Requires the free VISINVOKE Image Toolkit. Paying is never required to use Image Toolkit. AI Rebuild is optional.',
    statusLabel: 'Available',
    ctaRoute: '/dashboard/utilities/ai-rebuild',
    ctaLabel: 'VIEW AI REBUILD & PRICING',
  },
];

// Correction stage: the Tech Utilities entry is rendered as a dedicated
// full-width card near the bottom of the Utilities page (after the Free
// VISINVOKE Utilities section), visible to all signed-in users. It opens
// the Tech Utilities catalogue and does not run any utility from this page.

// ---------------------------------------------------------------------------
// FREE VISINVOKE UTILITIES — the approved free-utility catalogue.
// These are genuine full free utilities included with a VISINVOKE account. They
// are NOT trials and are NOT feature-limited editions. They are catalogue
// entries only: the website never runs a utility, so these cards carry no
// Launch / Open / Download / Coming Soon action. Every utility runs through
// VISINVOKE DCC, which each card states explicitly.
//
// Every capability below is taken from the current authoritative local project
// for that utility:
//   Backup Utility  — customer-portal/src/BackupUtilityPage.js
//   PDF Toolkit     — customer-portal/src/PdfToolkitPage.js
//   Image Toolkit   — current Utilities catalogue (still to be built; the list
//                     is labelled as planned scope, not existing capability)
//   File Converter  — D:\Vibe Work\VISINVOKE File Converter (docs/SUPPORTED_FORMATS.md)
//   USB Builder     — D:\Visinvoke Work\VISINVOKE USB Builder (README.md)
// ---------------------------------------------------------------------------
export const FREE_UTILITIES = [
  {
    icon: '💾',
    title: 'VISINVOKE Backup Utility',
    detailKey: 'backup-utility',
    accent: '#a855f7',
    description: 'Visual backup management for the folders that actually matter. Set up backup projects, send them to the drives you already own, and see what is protected, where it lives and when it last ran.',
    pointsLabel: 'What it does',
    points: [
      'Project-based backup jobs',
      'NAS, local, external drive & network share targets',
      'Quick one-off backups without a project',
      'Scheduled backups, retention & exclusions',
      'Restore to original or custom locations',
      'Backup verification, history & reports',
    ],
    status: 'Available',
    route: '/dashboard/utilities/backup',
  },
  {
    icon: '📄',
    title: 'VISINVOKE PDF Toolkit',
    detailKey: 'pdf-toolkit',
    accent: '#f87171',
    description: 'Everyday document work without an online converter. Combine, separate, shrink and reorganise PDF files, then keep a record of every operation you have run.',
    pointsLabel: 'What it does',
    points: [
      'Merge several PDFs into one document',
      'Split a PDF into separate files',
      'Compress: maximum quality, balanced or maximum compression',
      'Rotate pages and extract page selections',
      'Convert images into a PDF',
      'Operation history with output details',
    ],
    status: 'Available',
    route: '/dashboard/utilities/pdf-toolkit',
  },
  {
    icon: '🖼️',
    title: 'VISINVOKE Image Toolkit',
    detailKey: 'image-toolkit',
    accent: '#34d399',
    description: 'Prepare images for anything without hunting for the right dimensions. Add a file or a whole folder, tell VISINVOKE what the image is for — a social post, an A4 print, a website banner — and it sets the size, crop and quality for you. Everything runs on your own computer and your original files are never changed.',
    pointsLabel: 'What it does',
    points: [
      'Size for a purpose — social platforms, print and A-series, web, documents and screens',
      'Resize, crop, fit and pad; batch-process whole folders at once',
      'Convert between JPEG, PNG, WebP, TIFF, BMP, GIF and ICO',
      'Optimise for quality, or hit a target file size such as under 500 KB',
      'Add text or logo watermarks, single or tiled',
      'Create thumbnails and full social sets from one image',
      'Remove private metadata (location, device) with originals left untouched',
      'Local enhancement to sharpen and clean up soft images',
      'Save reusable recipes and choose where your outputs go',
    ],
    status: 'Available',
    // NO route: the Image Toolkit is DCC-local - "everything runs on your own computer" - exactly
    // like File Converter and USB Builder, which also declare none. FreeUtilityCard never reads
    // `route`; it renders RUNS VIA DCC. The old '/utilities/image-toolkit' matched no React route
    // and no static bundle, and existed only to be misread by the product registry as a portal
    // launch authority it never had.
    paidAddon: {
      note: 'high-detail cloud reconstruction for images that cannot be enlarged locally',
      href: '/dashboard/utilities/ai-rebuild',
    },
  },
  {
    icon: '🔁',
    title: 'VISINVOKE File Converter',
    detailKey: 'file-converter',
    accent: '#3b82f6',
    description: 'A local conversion workspace for images, camera RAW, documents, data, audio, video and subtitles. Files are converted on your own machine by verified engines — nothing is uploaded to a web converter.',
    pointsLabel: 'Working conversion paths',
    points: [
      'Images: JPEG, PNG, WebP, BMP, GIF, TIFF, ICO and more',
      'Camera RAW (CR2, CR3, NEF, ARW, DNG…) to JPEG, PNG, TIFF',
      'SVG / SVGZ to PNG, JPEG, TIFF or PDF',
      'Data: CSV, TSV, JSON, YAML, XLSX, ODS, DIF',
      'Text & Office content: TXT, Markdown, DOCX, ODT, HTML, EPUB',
      'Audio (WAV, FLAC, MP3, AAC, Opus) and video remux / transcode',
      'Subtitles: SRT, WebVTT, ASS, TTML',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🔌',
    title: 'VISINVOKE USB Builder',
    detailKey: 'usb-builder',
    accent: '#fb923c',
    description: 'Build, verify and maintain VISINVOKE-managed USB drives with strict device safety checks. Boot media is inspected and planned; destructive boot writing is deliberately not enabled.',
    pointsLabel: 'What it does',
    points: [
      'Windows disk & volume discovery with capacity, model and bus detail',
      'System / boot-disk protection and target revalidation',
      'Managed USB content with full SHA-256 verification',
      'Incremental add, change and remove updates',
      'Reusable build profiles and categories',
      'Build history and versioned manifests',
      'ISO / image inspection and boot compatibility planning',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '📊',
    title: 'VISINVOKE Presentation Studio',
    detailKey: 'presentation-studio',
    accent: '#eab308',
    description: 'A local slide and data-visualisation studio. It normally runs through the VISINVOKE DCC, and can also run directly in your browser — for locked-down business PCs where the DCC cannot be installed. Turn your figures, rough points or an existing deck into management, reporting and training presentations, with editable PowerPoint output.',
    pointsLabel: 'What it does',
    points: [
      'Build a full deck or a single slide from your data',
      'Business graphs and KPI cards chosen for your figures',
      'Improve an existing PowerPoint’s layout and readability',
      'Local autosave and recovery of your current work',
      'Editable .pptx export — real objects, not flat pictures',
      'Runs via the DCC, or directly in your browser — nothing uploaded, no account needed',
    ],
    status: 'Available',
    route: '/presentation-studio/index.html',
    launch: { label: 'LAUNCH IN BROWSER', url: '/presentation-studio/index.html' },
  },
  {
    icon: '🎨',
    title: 'VISINVOKE Image Composer',
    detailKey: 'image-composer',
    accent: '#6366f1',
    description: 'Combine separate images into one polished composition. Add a few images, move, resize, rotate and layer them, crop and add simple finishing, then export one finished image. Everything runs on your own computer, and the result drops straight into Presentation Studio, PowerPoint, a document or an email.',
    pointsLabel: 'What it does',
    points: [
      'Add several images to one canvas by button or drag and drop',
      'Move, resize with corner handles and rotate each image directly',
      'Layer with Bring Forward, Send Backward, Bring to Front and Send to Back',
      'Crop with edge and corner handles, flip, duplicate and delete',
      'Finishing touches: shadow, border, rounded corners and opacity',
      'Transparent, white or coloured canvas at 16:9, square, portrait or a custom size',
      'Export PNG, transparent PNG, JPG or WebP, or copy the image',
      'Undo and Redo across the whole composition',
    ],
    status: 'Available',
    route: '/image-composer/index.html',
    launch: { label: 'LAUNCH IN BROWSER', url: '/image-composer/index.html' },
  },
  {
    icon: '🔎',
    title: 'VISINVOKE People Explorer',
    detailKey: 'people-explorer',
    accent: '#14b8a6',
    description: 'Explore public information about people and the works they are connected to. Search across structured sources, inspect living and deceased cast lists, compare people, and see exactly where each fact came from.',
    pointsLabel: 'What it does',
    points: [
      'Universal search for people, television, movies, music, books and history',
      'Living and deceased people shown together with clear status labels',
      'Birth dates, death dates, current ages and age at death with date precision preserved',
      'Cause-of-death facts shown only when a source states them',
      'Full cast and crew exploration with filters, sorting and comparison',
      'Source, confidence and conflict details for important facts',
      'Fast private local caching, recent searches and favourites',
      'CSV copy for the current people or cast view',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🐾',
    title: 'VISINVOKE Animal Explorer',
    detailKey: 'animal-explorer',
    accent: '#0f9f8f',
    description: 'Explore animal names, accepted classification, relationships, licensed images and observation patterns. Sources, licences, uncertainty and significant taxonomy conflicts stay visible.',
    pointsLabel: 'What it does',
    points: [
      'Search common names, scientific names, synonyms, partial terms and close typos',
      'Navigate accepted taxonomy from kingdom to species',
      'Compare related animals using compatible evidence only',
      'Review sourced traits and file-specific image attribution',
      'Explore privacy-safe aggregated GBIF occurrence observations',
      'Keep favourites, recent searches, taxonomy and cache on your device',
      'See provider status, release metadata and meaningful conflicts',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🎬',
    title: 'VISINVOKE Plex Discovery',
    detailKey: 'plex-discovery',
    accent: '#8b5cf6',
    description: 'Rediscover what is already on your Plex server. Instead of scrolling past the same things, get a few strong choices from the media you already own. In development; Plex connection and discovery are being built.',
    pointsLabel: 'What it will do',
    points: [
      'A few strong recommendations from your own Plex libraries, not another wall of posters',
      'One free companion across the DCC desktop, Android phone and tablet, and Google TV',
      'Permanent taste with LOVE, LIKE, NEUTRAL, LESS and AVOID, plus hard exclusions',
      'Structural viewing characteristics derived only from your Plex data, never an outside source',
      'Your mood tonight kept separate from your permanent taste',
      'Watch Together for shared sessions across profiles',
      'Playback always handed back to Plex',
    ],
    status: 'Coming Soon',
    route: null,
  },
  {
    icon: '🎵',
    title: 'VISINVOKE Music Explorer',
    detailKey: 'music-explorer',
    accent: '#24e0c1',
    description: 'Explore artists, bands, member timelines, albums, recordings and musical works with source-aware MusicBrainz metadata. No streaming, no lyrics, no assumed cover-art rights.',
    pointsLabel: 'What it does',
    points: [
      'Search artists, bands, albums, singles, recordings, works and labels, or an MBID, ISRC or ISWC',
      'Read member timelines with join and leave dates, current and former members and rejoin periods',
      'Keep Work, Recording, Track, Release and Release Group distinct',
      'Browse discographies in first-release chronology with only the types present',
      'Follow composer, writer and lyricist credits and recording-to-work links',
      'Cross-link members to People Explorer for full biography',
      'Keep favourites, recent searches and a paced provider cache on your device',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🌍',
    title: 'VISINVOKE World Explorer',
    detailKey: 'world-explorer',
    accent: '#4cc2ff',
    description: 'Explore countries, cities, regions, landmarks and natural features with population years, languages, currencies, time zones, local maps and comparisons. Sources, licences and conflicts stay visible.',
    pointsLabel: 'What it does',
    points: [
      'Search countries, cities, regions, landmarks and features, or an ISO code, native name or City, Country',
      'Read population with its observation year, area, calculated density, languages and currencies',
      'See current local time computed on your device, with multi-zone countries never collapsed',
      'Compare countries, cities or regions without mixing incompatible population definitions',
      'Browse local open-geography maps with a text equivalent and disputed boundaries kept as source',
      'Keep favourites, recent searches and a bounded provider cache on your device',
      'Work offline from a small local pack; use online sources only to enrich details',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🖥️',
    title: 'VISINVOKE Screen Manager',
    detailKey: 'screen-manager',
    accent: '#38bdf8',
    description: 'Make one screen behave like many. Split a large display, ultrawide or multi-monitor setup into visual work areas, add persistent toolbars, keep applications inside their assigned areas, and have your whole layout restore itself every time Windows starts.',
    pointsLabel: 'What it does',
    points: [
      'Divide a display into visual work areas by drawing them, with no coordinates to enter',
      'Hard boundaries keep maximized and restored windows inside their work area',
      'Protected toolbars are reserved space that windows never open underneath',
      'Primary Work Area decides where new unassigned windows open',
      'Optionally remember which applications belong in which area',
      'Saved layout returns automatically after a reboot',
      'Monitor recovery restores a reconnected display instead of rebuilding it',
      'Profiles save different layouts and switch between them',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '📧',
    title: 'VISINVOKE Email',
    paidAddon: { href: '/email-ai', label: 'OPTIONAL AI WRITING ADD-ONS', note: 'Email remains full-version free' },
    detailKey: 'email-client',
    accent: '#3b82f6',
    description: 'A deliberately simple email program for Windows and for Android phones and tablets. Bring your old Outlook email across once on Windows, sort mail into your own folders, and let simple rules file new messages for you. Your mail is kept on your own device.',
    pointsLabel: 'What it does',
    points: [
      'Bring old Outlook email files (PST) across once, without changing the original files',
      'Duplicate protection means importing the same file twice never doubles your mail',
      'Create, rename and delete your own folders, and move messages between them',
      'Rules sort mail by who sent it, the subject, the message text or other details',
      'Rules run automatically on new mail, or on existing mail whenever you choose',
      'New, Reply, Reply All and Forward, with attachments and saved drafts',
      'Reading pane on the right or at the bottom, with sizes that stay as you set them',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '🧭',
    title: 'VISINVOKE Agent Control',
    detailKey: 'agent-control',
    accent: '#22d3ee',
    description: 'Control Claude and Codex with reusable rules, preflight checks, task controls, and simple visual setup. Tick ready-made rules or write your own in plain English, and Agent Control applies them for you without you editing any agent configuration files.',
    pointsLabel: 'What it does',
    points: [
      'Tick ready-made rules, each with a plain-English reason',
      'Write your own rules in plain English, with no configuration syntax',
      'Choose agents, scope and strength with simple chips',
      'Preflight checks before an agent edits anything',
      'Completion checks, with extra stages reported instead of started',
      'A Current Task the agents receive instead of giant prompts',
      'Optional ChatGPT help by copy and paste, with no AI built in',
      'Check Everything Is Working shows what is really active',
    ],
    status: 'Available',
    route: null,
  },
  {
    icon: '📚',
    title: 'VISINVOKE Books Explorer',
    detailKey: 'book-explorer',
    accent: '#6d67ff',
    description: 'Explore books, authors, editions and ISBNs in one fast, clean, ad-free Explorer. Search a title, an author or an ISBN, see every catalogued edition and compare them field by field, with your own private shelves, ratings, notes and reading goals.',
    pointsLabel: 'What it will do',
    points: [
      'Search titles, authors and ISBN-10 or ISBN-13 numbers',
      'Every catalogued edition with publisher, language, format and identifiers',
      'Compare two to four editions field by field',
      'Author bibliographies in publication order',
      'Series statements shown exactly as catalogued',
      'Private shelves, ratings, notes, lists and reading goals',
      'Sources visible on every record',
    ],
    // Self-Hosted Open Data programme product: Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🥗',
    title: 'VISINVOKE Food & Nutrition',
    detailKey: 'food-nutrition',
    accent: '#22c55e',
    description: 'Look up calories, nutrients and serving sizes for everyday and branded foods in one clean, ad-free reference, compare foods side by side and find foods high or low in any nutrient.',
    pointsLabel: 'What it will do',
    points: [
      'Search everyday foods and branded products',
      'Energy in kilocalories and kilojoules',
      'Macronutrients, vitamins and minerals',
      'Per 100 g or per published serving',
      'Compare foods side by side',
      'Find foods high or low in a nutrient',
      'Data type and source shown on every food',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🥫',
    title: 'VISINVOKE Packaged Food Scanner',
    detailKey: 'packaged-food-scanner',
    accent: '#10b981',
    description: 'Scan or type a barcode to see a packaged product\'s allergens, ingredients and nutrition, compare products and keep favourites, all from the VISINVOKE-hosted product database with no adverts.',
    pointsLabel: 'What it will do',
    points: [
      'Scan a barcode on Android or type it on Windows',
      'Allergens shown first',
      'Ingredients and nutrition per 100 g or per serving',
      'Nutri-Score and NOVA as published',
      'Compare products side by side',
      'Favourites kept on your device',
      'Open Food Facts attribution on every product',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🧠',
    title: 'VISINVOKE Knowledge Explorer',
    detailKey: 'knowledge-explorer',
    accent: '#0ea5e9',
    description: 'Search people, places, events, organisations and ideas and follow the facts that connect them, with On this day, categories and side-by-side comparisons, all from the VISINVOKE-hosted Knowledge Core.',
    pointsLabel: 'What it will do',
    points: [
      'Search anything by name',
      'Clear fact pages with dates and places',
      'Follow links and related entries',
      'On this day',
      'Explore by category',
      'Compare entries side by side',
      'Sources on every page',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🖼️',
    title: 'VISINVOKE Art & Museum Explorer',
    detailKey: 'art-museum-explorer',
    accent: '#d4a373',
    description: 'Explore artworks and artists from The Met and Smithsonian open collections, filter by museum, type and century, compare works and keep favourites, all from the VISINVOKE-hosted catalogue.',
    pointsLabel: 'What it will do',
    points: [
      'Search artists, subjects and titles',
      'Filter by museum, type and century',
      'Artwork records with dates and media',
      'Artist pages with their works',
      'Compare artworks side by side',
      'Favourites kept on your device',
      'Museum record linked on every artwork',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🧳',
    title: 'VISINVOKE Travel Guide',
    detailKey: 'travel-guide',
    accent: '#14b8a6',
    description: 'Read free travel guides for destinations around the world, with things to see, do, eat and places to stay, nearby places and a trip list on your device, all from the VISINVOKE-hosted guide library.',
    pointsLabel: 'What it will do',
    points: [
      'Search destinations worldwide',
      'Guides organised into clear sections',
      'See, Do, Buy, Eat, Drink and Sleep listings',
      'Hours, prices and coordinates where given',
      'Nearby places and sub-destinations',
      'A trip list kept on your device',
      'Authors and last-edited date on every guide',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🪐',
    title: 'VISINVOKE Space Explorer',
    detailKey: 'space-explorer',
    accent: '#818cf8',
    description: 'Explore confirmed exoplanets, their star systems and mission candidates, with discovery timelines, charts and planet comparisons, all from the VISINVOKE-hosted snapshot of NASA Exoplanet Archive tables.',
    pointsLabel: 'What it will do',
    points: [
      'Search planets, star systems and candidates',
      'Planet and system pages',
      'Candidates kept separate from confirmed planets',
      'Discoveries over time',
      'Charts of planet properties',
      'Compare up to four planets',
      'Snapshot date and citations on every page',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🌦️',
    title: 'VISINVOKE Historical Weather & Climate Explorer',
    detailKey: 'climate-explorer',
    accent: '#38bdf8',
    description: 'Explore long-term weather records for U.S. stations: 1991-2020 normals, record highs and lows with dates, long-term trends, any year day by day and this day in history, all from VISINVOKE-hosted NOAA data.',
    pointsLabel: 'What it will do',
    points: [
      'Find a U.S. weather station',
      '1991-2020 monthly normals',
      'Record highs, lows and wettest days with dates',
      'Long-term temperature and rainfall trends',
      'Any year day by day',
      'This day in history',
      'Compare up to four stations',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
  {
    icon: '🍳',
    title: 'VISINVOKE Recipes',
    detailKey: 'recipes',
    accent: '#4ade80',
    description: 'Find recipes by cuisine, diet, meal and time, scale servings, cook step by step with timers, plan meals and build a shopping list, all from the VISINVOKE-hosted recipe library.',
    pointsLabel: 'What it will do',
    points: [
      'Search by cuisine, diet, meal, method and time',
      'Scale servings with metric and US units',
      'Step-by-step cooking mode with timers',
      'What can I cook from what you have',
      'Seven-day meal planner and shopping list',
      'Your own recipes kept on your device',
      'Estimated nutrition for each recipe',
    ],
    // Self-Hosted Open Data programme product: FREE, and Coming Soon until the owner release gate opens its
    // dashboard surface (config/openDataRelease.js reads the backend release overlay; no rebuild needed).
    status: 'Coming Soon',
    releaseGate: 'open-data',
    route: null,
  },
];

// ---------------------------------------------------------------------------
// Paid operational product access wiring (Service Command Centre).
// Access is derived from the REAL signed-in user entitlement
// object exposed by AuthContext (user.entitlements.<key>), using the same
// convention the rest of the app uses. See getCombatProOwned in
// combatProTiers.js, which treats a `true` entitlement as owned. Nothing here
// grants access on its own: isEntitlementActive returns false whenever the
// entitlement is absent or is not in an active/trialing state, so no user can
// launch a product without a real active entitlement.
// ---------------------------------------------------------------------------

// Real entitlement key the backend populates on user.entitlements.
export const SERVICE_COMMAND_CENTRE_ENTITLEMENT = 'service_command_centre_access';

// Stripe lookup key (stable across environments, NOT a price ID). The frontend
// NEVER sends this: the backend Service Command Centre checkout route resolves
// every Stripe Price server-side. The frontend may send only the approved
// staff-limit and cloud-tier selectors. It is kept here for documentation only.
const SERVICE_COMMAND_CENTRE_LOOKUP_KEY = 'service_command_centre_monthly_usd_65';

// Backend checkout route for the Service Command Centre subscription. The
// frontend posts only the selected staff/cloud keys; the backend resolves Prices
// (from lookup key service_command_centre_monthly_usd_65 or STRIPE_SCC_PRICE_ID),
// attaches metadata, and returns { checkout_url }.
const SERVICE_COMMAND_CENTRE_CHECKOUT_ROUTE = '/api/payments/create-service-command-centre';

// The real in-app Service Command Centre route registered in App.js. Used only
// when the signed-in user holds an active entitlement.
export const SCC_LAUNCH_ROUTE = '/dashboard/utilities/service-command-centre';

// Checkout metadata to attach WHEN a checkout helper that supports metadata is
// wired. Dormant today - no checkout call is made from this page.
const SERVICE_COMMAND_CENTRE_CHECKOUT_META = {
  product_key:       'service_command_centre',
  visinvoke_product: 'service_command_centre',
  entitlement_key:   'service_command_centre_access',
  price_key:         'service_command_centre_monthly_usd_65',
  source:            'utilities_page',
};

// Checkout seam. Flip this ONLY when the real backend seam exists - never
// hardcode active access.
//   SCC_CHECKOUT_SEAM_READY: the backend Service Command Centre checkout route
//     (SERVICE_COMMAND_CENTRE_CHECKOUT_ROUTE) is live and returns a checkout_url,
//     so this is true. Activate Service Command Centre starts real checkout.
export const SCC_CHECKOUT_SEAM_READY = true;

// Real entitlement resolver. Returns true ONLY when the signed-in user's
// entitlement for `key` is in an active state. Mirrors the app's existing
// `=== true` convention (combatProTiers.getCombatProOwned) and also accepts the
// documented subscription statuses (active / trialing) in case the backend
// exposes the entitlement as a status string or an object with a status. Every
// inactive status (incomplete, incomplete_expired, past_due, canceled, unpaid,
// paused) and every absent entitlement resolves to false. No optional chaining
// / nullish operators (project constraint).
export function isEntitlementActive(user, key) {
  if (!user) return false;
  const ents = user.entitlements;
  if (!ents) return false;
  const raw = ents[key];
  if (raw === true) return true;
  if (typeof raw === 'string') {
    return raw === 'active' || raw === 'trialing';
  }
  if (raw && typeof raw === 'object') {
    if (raw.active === true) return true;
    const st = raw.status;
    if (typeof st === 'string') {
      return st === 'active' || st === 'trialing';
    }
  }
  return false;
}

// Service Command Centre checkout. The frontend posts only staff_limit and
// cloud_tier; the backend resolves all Stripe Prices server-side from the
// lookup key (or STRIPE_SCC_PRICE_ID) and returns { checkout_url }. On success
// the browser is redirected to Stripe. The lookup key and metadata below are
// documentation only and are NEVER sent in the request body.
// callbacks: { staffLimit, cloudTier, onError(message), onSettled() }. On successful redirect the page
// navigates away, so onSettled fires only on the no-url / error paths.
export function startPaidProductCheckout(kind, callbacks) {
  const cbs = callbacks || {};
  const onError = typeof cbs.onError === 'function' ? cbs.onError : function () {};
  const onSettled = typeof cbs.onSettled === 'function' ? cbs.onSettled : function () {};
  const staffLimit = Number(cbs.staffLimit || 10);
  const cloudTier = typeof cbs.cloudTier === 'string' ? cbs.cloudTier : 'none';

  // Documentation only - resolved/attached server-side, never sent from here.
  const docLookupKey = SERVICE_COMMAND_CENTRE_LOOKUP_KEY;
  const docMeta = SERVICE_COMMAND_CENTRE_CHECKOUT_META;

  API.post(SERVICE_COMMAND_CENTRE_CHECKOUT_ROUTE, {
    staff_limit: staffLimit,
    cloud_tier: cloudTier,
  })
    .then(function (r) {
      if (r && r.data && typeof r.data.checkout_url === 'string' && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      onError('We could not start checkout. Please try again.');
      onSettled();
    })
    .catch(function (e) {
      let httpStatus = 0;
      if (e && e.response && typeof e.response.status === 'number') {
        httpStatus = e.response.status;
      }
      let errorCode = '';
      if (e && e.response && e.response.data && typeof e.response.data.error === 'string') {
        errorCode = e.response.data.error;
      }
      if (httpStatus === 409 && errorCode === 'immediate_access_consent_required') {
        // NOT "already owned". The server is asking for the immediate-supply acknowledgement first.
        // Hand the whole payload to the caller so it can render the shared consent block and retry;
        // never present this as an error the customer cannot act on.
        if (typeof cbs.onConsentRequired === 'function') {
          cbs.onConsentRequired(e);
        } else {
          onError('Please confirm the acknowledgement shown before continuing.');
        }
      } else if (httpStatus === 409) {
        // Backend reports the account already has Service Command Centre. Do NOT
        // fake local access - the entitlement gate flips to Active from
        // /v1/auth/me. Prompt a refresh to pick up the updated state.
        onError('This account already has Service Command Centre. Refresh the page to update your access.');
      } else {
        let msg = 'We could not start checkout. Please try again.';
        if (e && e.response && e.response.data && typeof e.response.data.error === 'string') {
          msg = e.response.data.error;
        }
        onError(msg);
      }
      onSettled();
    });

  return { lookupKey: docLookupKey, metadata: docMeta };
}

// Paid operational product card (Service Command Centre).
// Combat PRO-style ownership/pricing layout: eyebrow, status pill, product
// name, price, monthly billing label, description, feature list, and a
// state-aware full-width action button. Section-coloured via util.accent.
function PaidProductCard({ util }) {
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const accent = util.accent || '#38bdf8';

  // Local checkout state for the Service Command Centre Activate button only.
  const [sccBusy, setSccBusy] = useState(false);
  const [sccError, setSccError] = useState('');

  let statusLabel = 'Coming Soon';
  let statusTone = '#fbbf24';
  let btnLabel = 'Coming Soon';
  let btnEnabled = false;
  let onClick = null;
  let sccDccAccess = false;

  // Service Command Centre launches ONLY when the signed-in user holds a real
  // active service_command_centre_access entitlement. Otherwise it is
  // Subscription Required, and Activate can only start checkout once a real
  // backend checkout seam exists (SCC_CHECKOUT_SEAM_READY).
  const sccActive = isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT);
  if (sccActive) {
    statusLabel = 'Active';
    statusTone = '#34d399';
    // SCC launches from the VISINVOKE Desktop Command Centre, not the web
    // dashboard, so no web launch control is shown here.
    sccDccAccess = true;
  } else {
    statusLabel = 'Subscription Required';
    statusTone = '#fbbf24';
    // The backend checkout route is live (SCC_CHECKOUT_SEAM_READY). Clicking
    // Activate starts real Stripe checkout: it posts an empty body to the
    // backend, which resolves the price server-side and returns a checkout_url
    // to redirect to. No lookup key, price ID or plan slug is sent.
    btnLabel = sccBusy ? 'Starting Checkout...' : 'Activate Service Command Centre';
    btnEnabled = SCC_CHECKOUT_SEAM_READY && !sccBusy;
    onClick = SCC_CHECKOUT_SEAM_READY
      ? () => {
          setSccError('');
          setSccBusy(true);
          startPaidProductCheckout('scc', {
            onError: (m) => { setSccError(m); },
            onSettled: () => { setSccBusy(false); },
          });
        }
      : null;
  }

  const cardStyle = {
    ...ug.ppCard,
    border: '1px solid ' + hexA(accent, 0.4),
    boxShadow: '0 10px 44px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ' + hexA(accent, 0.12) + ', 0 0 28px ' + hexA(accent, 0.14),
  };

  return (
    <div className="util-reveal" style={cardStyle}>
      <div style={ug.ppTopRow}>
        <span style={{ ...ug.ppEyebrow, color: accent }}>{util.eyebrow}</span>
        <span style={{ ...ug.ppStatusPill, color: statusTone, borderColor: hexA(statusTone, 0.5), background: hexA(statusTone, 0.12) }}>
          <span style={{ ...ug.ppStatusDot, background: statusTone }} />
          {statusLabel}
        </span>
      </div>

      {/* The card fills the Main Programs width, so its information is laid out
          in two columns: product identity / pricing / action on the left, the
          capability list on the right. Columns live in the .pp-split class and
          collapse to the original single-column stack on narrow widths. */}
      <div className="pp-split">
        <div style={ug.ppIdentityCol}>
          <div style={ug.ppNameRow}>
            <span style={{ ...ug.ppIcon, color: accent }}>{util.icon}</span>
            <span style={ug.ppName}>{util.title}</span>
          </div>

          <div style={ug.ppPriceRow}>
            <span style={ug.ppPrice}>{util.price}</span>
            <span style={ug.ppBilling}>{util.billing}</span>
          </div>

          <p style={ug.ppDesc}>{util.body}</p>

          <div style={ug.ppFooter}>
            {sccDccAccess ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#e6edf5', lineHeight: 1.4 }}>Available in VISINVOKE Desktop Command Centre</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.62)', lineHeight: 1.5, marginTop: 4 }}>Use DCC on your computer to launch Service Command Centre.</div>
              </div>
            ) : btnEnabled ? (
              <button
                className="util-btn-hover"
                style={{ ...ug.ppBtn, background: accent, color: '#04121f' }}
                onClick={onClick}
              >
                {btnLabel}
              </button>
            ) : (
              <button style={ug.ppBtnDisabled} disabled>
                {btnLabel}
              </button>
            )}
            {sccError ? (
              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#fca5a5' }}>
                {sccError}
              </div>
            ) : null}
          </div>
        </div>

        <ul style={ug.ppFeatureGrid}>
          {util.features.map((f, i) => (
            <li key={i} style={ug.ppFeatureItem}>
              <span style={{ ...ug.ppDot, background: accent }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WEBSITE INTELLIGENCE - dedicated premium platform section.
// A standalone bordered section (its own identity, distinct from the Windows
// Knowledge Centre) presenting the VISINVOKE Website Capture Suite as a
// professional platform. Palette: deep navy / slate blue / electric cyan with
// white typography. Layout: hero description, highlight badge strip, then four
// responsive information columns (Overview, Capabilities, Ideal For, Future
// Roadmap). The existing Coming Soon button and pricing are retained unchanged.
// ---------------------------------------------------------------------------
export function WebsiteIntelligenceSection() {
  const accent = '#22d3ee';

  const wiOverview = [
    'Browser-based website discovery',
    'Recursive website crawling',
    'Intelligent queue management',
    'Crawl validation',
    'Structured website database',
    'Website documentation',
    'Crawl history',
    'AI-ready structured capture',
    'Professional website auditing',
    'Website inventory',
    'Long-term website archiving',
    'Migration planning',
    'Compliance reviews',
    'Structured content capture',
    'Browser rendering',
    'Website analysis',
  ];

  const wiCapabilities = [
    {
      group: 'Discovery',
      items: ['Intelligent Discovery', 'Recursive Crawling', 'Queue Management', 'Crawl Validation'],
    },
    {
      group: 'Analysis',
      items: ['Metadata Extraction', 'Canonical Detection', 'JavaScript Rendering', 'Crawl Reporting'],
    },
    {
      group: 'Management',
      items: ['Multi Projects', 'Multi Websites', 'Saved Crawl Profiles', 'Crawl History'],
    },
    {
      group: 'Control',
      items: ['robots.txt Support', 'URL Include Rules', 'URL Exclude Rules', 'Internal / External Link Control'],
    },
  ];

  const wiIdealFor = [
    'Website Developers',
    'Web Design Agencies',
    'SEO Professionals',
    'Digital Marketing',
    'IT Consultants',
    'MSPs',
    'Compliance Teams',
    'Researchers',
    'Website Owners',
    'Corporate Organisations',
    'Government',
    'Education',
    'Security Professionals',
    'Migration Specialists',
  ];

  const wiRoadmap = [
    'Website Screenshots',
    'Asset Capture',
    'CSS Analysis',
    'JavaScript Analysis',
    'Media Discovery',
    'PDF Capture',
    'Scheduled Crawls',
    'Change Detection',
    'Search Indexing',
    'AI Dataset Builder',
    'Export Centre',
    'Reporting Suite',
  ];

  const wiHighlights = [
    'Unlimited Projects',
    'Unlimited Websites',
    'Recursive Crawling',
    'Browser Rendering',
    'JavaScript Support',
    'Crawl History',
    'Crawl Validation',
    'AI Ready',
  ];

  return (
    <div style={ug.wiSection}>
      {/* Section header */}
      <div style={ug.wiHeader}>
        <div style={ug.wiHeadingRow}>
          <span style={ug.wiHeaderIcon}>{'\u{1F310}'}</span>
          <h2 style={ug.wiHeading}>WEBSITE <span style={ug.wiHeadingAccent}>INTELLIGENCE</span></h2>
        </div>
        <p style={ug.wiSubtitle}>Professional website discovery, crawling, auditing, documentation and analysis.</p>
      </div>

      {/* Featured product block */}
      <div style={ug.wiProduct}>
        {/* Product title row: name + price + button */}
        <div style={ug.wiProductTopRow}>
          <div style={ug.wiProductNameBlock}>
            <div style={{ ...ug.wiColLabel, color: accent }}>PROFESSIONAL WEBSITE INTELLIGENCE PLATFORM</div>
            <div style={ug.wiProductName}>VISINVOKE Website Capture Suite</div>
          </div>
          <div style={ug.wiProductRight}>
            <div style={ug.wiPriceRow}>
              <span style={ug.wiPrice}>$14.99 USD</span>
              <span style={ug.wiBilling}>Per Month</span>
            </div>
            <div style={ug.wiPriceNote}>Unlimited projects &middot; ongoing updates</div>
            <button style={ug.wiBtn} disabled>Coming Soon</button>
          </div>
        </div>

        {/* Hero description */}
        <div style={ug.wiHero}>
          <p style={ug.wiHeroText}>
            Automatically discover, crawl, analyse and document websites into a structured database ready for auditing, migration, compliance, AI processing, documentation and long-term website management.
          </p>
          <p style={ug.wiHeroText}>
            VISINVOKE Website Capture Suite provides intelligent browser-based website discovery, recursive crawling, structured capture and powerful reporting for professionals managing websites of any size.
          </p>
        </div>

        {/* Highlight badges strip */}
        <div className="wi-badges" style={ug.wiBadges}>
          {wiHighlights.map((h, i) => (
            <span key={i} style={ug.wiBadge}>
              <span style={{ ...ug.wiBadgeCheck, color: accent }}>{'\u2713'}</span>
              {h}
            </span>
          ))}
        </div>

        {/* Four premium information cards - two per row, one component family */}
        <div className="wi-cards" style={ug.wiCards}>
          {/* Overview - deep blue */}
          <div style={{ ...ug.wiCard, borderColor: hexA('#3b82f6', 0.4), boxShadow: '0 8px 30px rgba(0,0,0,0.4), 0 0 22px ' + hexA('#3b82f6', 0.12) }}>
            <div style={ug.wiCardHead}>
              <span style={ug.wiCardIcon}>{'\u{1F50D}'}</span>
              <span style={{ ...ug.wiCardTitle, color: '#93c5fd' }}>Overview</span>
            </div>
            <div style={{ ...ug.wiCardDivider, background: hexA('#3b82f6', 0.35) }} />
            <div style={ug.wiCardBody}>
              <p style={ug.wiCardIntro}>Intelligent browser-based website capture built as a structured, AI-ready platform.</p>
              <ul style={ug.wiList}>
                {wiOverview.map((f, i) => (
                  <li key={i} style={ug.wiListItem}>
                    <span style={{ ...ug.wiDot, background: '#3b82f6' }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Capabilities - electric cyan */}
          <div style={{ ...ug.wiCard, borderColor: hexA('#22d3ee', 0.4), boxShadow: '0 8px 30px rgba(0,0,0,0.4), 0 0 22px ' + hexA('#22d3ee', 0.12) }}>
            <div style={ug.wiCardHead}>
              <span style={ug.wiCardIcon}>{'\u{2699}'}</span>
              <span style={{ ...ug.wiCardTitle, color: '#67e8f9' }}>Capabilities</span>
            </div>
            <div style={{ ...ug.wiCardDivider, background: hexA('#22d3ee', 0.35) }} />
            <div style={ug.wiCardBody}>
              <div style={ug.wiCapMatrix}>
                {wiCapabilities.map((grp, gi) => (
                  <div key={gi} style={ug.wiCapGroup}>
                    <div style={ug.wiCapGroupName}>{grp.group}</div>
                    <ul style={ug.wiListSingle}>
                      {grp.items.map((f, i) => (
                        <li key={i} style={ug.wiListItem}>
                          <span style={{ ...ug.wiDot, background: '#22d3ee' }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Perfect For - purple */}
          <div style={{ ...ug.wiCard, borderColor: hexA('#a855f7', 0.4), boxShadow: '0 8px 30px rgba(0,0,0,0.4), 0 0 22px ' + hexA('#a855f7', 0.12) }}>
            <div style={ug.wiCardHead}>
              <span style={ug.wiCardIcon}>{'\u{1F465}'}</span>
              <span style={{ ...ug.wiCardTitle, color: '#d8b4fe' }}>Perfect For</span>
            </div>
            <div style={{ ...ug.wiCardDivider, background: hexA('#a855f7', 0.35) }} />
            <div style={ug.wiCardBody}>
              <ul style={ug.wiList}>
                {wiIdealFor.map((f, i) => (
                  <li key={i} style={ug.wiListItem}>
                    <span style={{ ...ug.wiDot, background: '#a855f7' }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Growing Platform - amber / gold */}
          <div style={{ ...ug.wiCard, borderColor: hexA('#f59e0b', 0.4), boxShadow: '0 8px 30px rgba(0,0,0,0.4), 0 0 22px ' + hexA('#f59e0b', 0.12) }}>
            <div style={ug.wiCardHead}>
              <span style={ug.wiCardIcon}>{'\u{1F680}'}</span>
              <span style={{ ...ug.wiCardTitle, color: '#fcd34d' }}>Growing Platform</span>
            </div>
            <div style={{ ...ug.wiCardDivider, background: hexA('#f59e0b', 0.35) }} />
            <div style={ug.wiCardBody}>
              <p style={ug.wiCardIntro}>Planned platform growth, included as the suite evolves.</p>
              <ul style={ug.wiList}>
                {wiRoadmap.map((f, i) => (
                  <li key={i} style={ug.wiListItem}>
                    <span style={{ ...ug.wiDotHollow, borderColor: '#f59e0b' }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VISINVOKE CONTENT PLANNER section. Same structure as the Website Capture
// Suite block above it (section header, main product presentation, capability
// strip) followed by the two capacity tier cards. Built from the page's
// existing card language with the Content Planner accent.
// ---------------------------------------------------------------------------
function ContentPlannerTierCard({ tier }) {
  const accent = CONTENT_PLANNER_ACCENT;
  const cardStyle = {
    ...ug.cpTierCard,
    border: '1px solid ' + hexA(accent, 0.34),
    boxShadow: '0 10px 44px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ' + hexA(accent, 0.12) + ', 0 0 24px ' + hexA(accent, 0.12),
  };
  return (
    <div className="util-card-hover util-reveal" style={cardStyle}>
      <span style={{ ...ug.cpTierEyebrow, color: hexA(accent, 0.95) }}>{tier.eyebrow}</span>
      <div style={ug.cpTierTitle}>{tier.title}</div>
      <div style={ug.cpTierPriceRow}>
        <span style={ug.cpTierPrice}>{tier.price}</span>
        <span style={ug.cpTierBilling}>{tier.billing}</span>
      </div>
      <div style={{ ...ug.cpTierCapacity, borderColor: hexA(accent, 0.35), color: '#ffffff' }}>
        <span style={{ ...ug.cpTierCapacityDot, background: accent }} />
        {tier.capacity}
      </div>
      <ul style={ug.cpTierList}>
        {tier.features.map((f, i) => (
          <li key={i} style={ug.cpTierItem}>
            <span style={{ ...ug.cpTierDot, background: accent }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ContentPlannerSection() {
  const accent = CONTENT_PLANNER_ACCENT;
  // Server-authoritative launch gate. Fails closed to Not Available Yet.
  const cpLaunch = useContentPlannerLaunch();

  return (
    <div style={ug.cpSection}>
      {/* Section header */}
      <div style={ug.cpHeader}>
        <div style={ug.cpHeadingRow}>
          <span style={ug.cpHeaderIcon}>{'\u{1F4C5}'}</span>
          <h2 style={ug.cpHeading}>CONTENT <span style={ug.cpHeadingAccent}>PLANNER</span></h2>
        </div>
        <p style={ug.cpSubtitle}>A standalone paid VISINVOKE utility for planning, scheduling and publishing business content.</p>
      </div>

      {/* Main product presentation */}
      <div style={ug.cpProduct}>
        <div style={ug.cpProductTopRow}>
          <div style={ug.cpProductNameBlock}>
            <div style={{ ...ug.cpColLabel, color: accent }}>PAID VISINVOKE UTILITY &middot; MONTHLY SUBSCRIPTION</div>
            <div style={ug.cpProductName}>VISINVOKE Content Planner</div>
          </div>
          <div style={ug.cpProductRight}>
            <div style={ug.cpPriceRow}>
              <span style={ug.cpPrice}>$5.99 &ndash; $8.99 USD</span>
              <span style={ug.cpBilling}>Per Month</span>
            </div>
            <div style={ug.cpPriceNote}>Two capacity tiers &middot; identical feature set</div>
            <button style={ug.cpBtn} disabled>{cpLaunch.statusLabel}</button>
          </div>
        </div>

        <div style={ug.cpHero}>
          <p style={ug.cpHeroText}>
            VISINVOKE Content Planner is its own paid VISINVOKE product, not part of a bundle and not a free utility.
            It runs inside VISINVOKE DCC and covers the whole content workflow: bulk import from documents and
            spreadsheets, a reusable content library, review and approval, campaigns, a scheduling calendar, native
            Windows media handling with media rotation, and analytics.
          </p>
          <p style={ug.cpHeroText}>
            Scheduled publishing is cloud-backed by design. Committed schedules run on VISINVOKE cloud infrastructure so
            publishing continues when your PC and DCC are completely shut down. That cloud and media capacity is the only
            difference between the two tiers below.
          </p>
        </div>

        {/* Complete capability set — identical on both tiers */}
        <div className="cp-badges">
          {CONTENT_PLANNER_FEATURES.map((f, i) => (
            <span key={i} style={ug.cpBadge}>
              <span style={{ ...ug.cpBadgeCheck, color: accent }}>{'✓'}</span>
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Equal-feature statement */}
      <div className="util-reveal" style={ug.cpEqualPanel}>
        <span style={ug.cpEqualIcon}>{'⚖'}</span>
        <p style={ug.cpEqualText}>
          <span style={ug.cpEqualStrong}>Both tiers include the same complete Content Planner feature set.</span>{' '}
          Media+ is not a bigger edition and unlocks nothing extra &mdash; it exists because scheduled publishing runs in
          the cloud, and larger media libraries need materially more cloud capacity.
        </p>
      </div>

      {/* Two capacity tiers */}
      <div className="cp-tiers">
        {CONTENT_PLANNER_TIERS.map((tier, i) => (
          <ContentPlannerTierCard key={i} tier={tier} />
        ))}
      </div>

      <p style={ug.cpFootnote}>
        {cpLaunch.customerMessage}
      </p>
      <p style={ug.cpFootnote}>
        Cloud media capacities are launch-planning values held in the Content Planner product configuration.
      </p>
    </div>
  );
}

export function ActiveUtilCard({ util }) {
  const navigate = useNavigate();
  if (util.paid) {
    return <PaidProductCard util={util} />;
  }
  const handleClick = () => {
    if (util.buttonActive && util.buttonUrl) {
      // Use client-side React Router navigation (same method as the Registry
      // and Free cards) instead of a full browser navigation. Strip any
      // absolute origin so we navigate to the relative app path, which avoids
      // a full page reload and the auth re-initialisation it would trigger.
      const target = util.buttonUrl.replace(/^https?:\/\/[^/]+/, '');
      navigate(target);
    }
  };

  return (
    <div className="util-card-hover util-reveal" style={ug.activeCard}>
      <div style={ug.cardTop}>
        <div style={ug.cardIcon}>{util.icon}</div>
        <div>
          <div style={ug.activeCardTitle}>{util.title}</div>
          <div style={ug.activeCardSubtitle}>{util.subtitle}</div>
        </div>
      </div>
      <p style={ug.cardBody}>{util.body}</p>
      <ul style={ug.featureList}>
        {util.features.map((f, i) => (
          <li key={i} style={ug.featureItem}>
            <span style={ug.featureDotActive} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div style={ug.activeCardFooter}>
        {util.buttonActive ? (
          <button className="util-btn-hover" style={ug.launchBtn} onClick={handleClick}>
            {util.buttonLabel}
          </button>
        ) : (
          <button style={ug.comingSoonBtn} disabled>
            {util.buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// Darken a hex colour by a factor (0..1). factor 0.4 => 40% of original
// brightness. Used to build rich, fully-coloured card backgrounds from the
// category accent so the ENTIRE card carries the colour.
function darkenHex(hex, factor) {
  let h = (hex || '#888888').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = Math.round(parseInt(h.substring(0, 2), 16) * factor);
  const g = Math.round(parseInt(h.substring(2, 4), 16) * factor);
  const b = Math.round(parseInt(h.substring(4, 6), 16) * factor);
  const to2 = n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// FULL-COLOUR category card. The entire card background is a rich gradient of
// the category colour - not a navy card with coloured accents. Each card is
// recognisable purely by its colour. White title, light description, readable
// status, matching coloured button and divider.
export function ThemedCard({ accent, icon, title, description, status, buttonLabel, onClick, freeBadge }) {
  const a = accent || '#94a3b8';
  const enabled = typeof onClick === 'function';

  // Full coloured gradient across the whole card.
  const top = darkenHex(a, 0.50);
  const bottom = darkenHex(a, 0.30);
  const bg = `linear-gradient(160deg, ${top} 0%, ${bottom} 100%)`;
  const restShadow = `0 8px 26px ${hexA(a, 0.28)}, 0 2px 10px rgba(0,0,0,0.4)`;
  const hoverShadow = `0 16px 40px ${hexA(a, 0.45)}, 0 4px 14px rgba(0,0,0,0.45)`;

  const onEnter = e => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = hoverShadow;
    e.currentTarget.style.borderColor = hexA('#ffffff', 0.35);
  };
  const onLeave = e => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = restShadow;
    e.currentTarget.style.borderColor = hexA('#ffffff', 0.18);
  };

  return (
    <div
      style={{
        ...ug.tcCard,
        background: bg,
        border: `1px solid ${hexA('#ffffff', 0.18)}`,
        boxShadow: restShadow,
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* top sheen */}
      <div style={ug.tcSheen} />

      {/* Header: icon + title */}
      <div style={ug.tcHeader}>
        <div style={ug.tcIcon}>{icon}</div>
        <div style={ug.tcTitle}>{title}</div>
      </div>

      {/* Description */}
      <div style={ug.tcDesc}>{description}</div>

      {/* Footer */}
      <div style={{ ...ug.tcFooter, borderTop: `1px solid ${hexA('#ffffff', 0.18)}` }}>
        {freeBadge ? (
          <div style={ug.tcFreeBadge}>
            <span style={ug.tcFreeDot} />
            FREE FOR ALL PAID VISINVOKE USERS
          </div>
        ) : null}

        <div style={ug.tcStatusRow}>
          <span style={{ ...ug.tcStatusDot, background: '#ffffff', boxShadow: '0 0 8px rgba(255,255,255,0.7)' }} />
          <span style={ug.tcStatusText}>{status}</span>
        </div>

        <button
          style={{
            ...ug.tcBtn,
            background: enabled ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)',
            color: enabled ? '#ffffff' : 'rgba(255,255,255,0.5)',
            border: `1px solid ${hexA('#ffffff', enabled ? 0.32 : 0.14)}`,
            cursor: enabled ? 'pointer' : 'not-allowed',
          }}
          onClick={enabled ? onClick : undefined}
          disabled={!enabled}
          onMouseEnter={e => { if (enabled) { e.currentTarget.style.background = 'rgba(255,255,255,0.26)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={e => { if (enabled) { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; e.currentTarget.style.borderColor = hexA('#ffffff', 0.32); e.currentTarget.style.transform = 'translateY(0)'; } }}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FREE UTILITY CARD — the large catalogue card used on the Free VISINVOKE
// Utilities page. Same full-colour card language as ThemedCard (accent gradient,
// sheen, white typography), sized for a five-across desktop row with room for
// real capability information.
//
// These cards are INFORMATIONAL. The website never runs a utility, so there is
// no Launch / Open / Download / Coming Soon action here. Every card states
// RUNS VIA DCC instead.
// ---------------------------------------------------------------------------
export function FreeUtilityCard({ accent, icon, title, description, points, pointsLabel, status: declaredStatus, launch, paidAddon, detailKey, releaseGate }) {
  const openDataRelease = useOpenDataReleaseState(releaseGate === 'open-data');
  const status = releaseGate === 'open-data' ? openDataStatusLabel(openDataRelease, detailKey, 'dashboard') : declaredStatus;
  const a = accent || '#94a3b8';

  const top = darkenHex(a, 0.50);
  const bottom = darkenHex(a, 0.30);
  const bg = `linear-gradient(160deg, ${top} 0%, ${bottom} 100%)`;
  const restShadow = `0 8px 26px ${hexA(a, 0.28)}, 0 2px 10px rgba(0,0,0,0.4)`;
  const hoverShadow = `0 16px 40px ${hexA(a, 0.45)}, 0 4px 14px rgba(0,0,0,0.45)`;

  const onEnter = e => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = hoverShadow;
    e.currentTarget.style.borderColor = hexA('#ffffff', 0.35);
  };
  const onLeave = e => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = restShadow;
    e.currentTarget.style.borderColor = hexA('#ffffff', 0.18);
  };

  return (
    <div
      style={{
        ...ug.fucCard,
        background: bg,
        border: `1px solid ${hexA('#ffffff', 0.18)}`,
        boxShadow: restShadow,
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div style={ug.tcSheen} />

      <div style={ug.fucHeader}>
        <div style={ug.fucIcon}>{icon}</div>
        <div style={ug.fucTitle}>{title}</div>
      </div>

      <div style={ug.fucDesc}>{description}</div>

      <div style={ug.fucPointsWrap}>
        <div style={ug.fucPointsLabel}>{pointsLabel}</div>
        <ul style={ug.fucPoints}>
          {points.map((p, i) => (
            <li key={i} style={ug.fucPoint}>
              <span style={ug.fucPointDot} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ ...ug.fucFooter, borderTop: `1px solid ${hexA('#ffffff', 0.18)}` }}>
        <div style={ug.fucStatusRow}>
          <FreeBadge variant="onColor" size="sm" />
          <AvailabilityBadge status={status} variant="onColor" size="sm" />
        </div>
        {launch ? (
          <>
            <button
              type="button"
              onClick={() => { if (typeof window !== 'undefined') window.open(launch.url, '_blank', 'noopener'); }}
              style={{
                ...ug.fucDccBadge,
                cursor: 'pointer',
                background: a,
                color: '#0b1220',
                border: `1px solid ${hexA('#ffffff', 0.55)}`,
                boxShadow: `0 4px 16px ${hexA(a, 0.5)}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
              aria-label={`Launch ${title} in your browser`}
            >
              <span style={ug.fucDccIcon}>{'▸'}</span>
              {launch.label || 'LAUNCH'}
            </button>
            <div style={ug.fucDccBadge}>
              <span style={ug.fucDccIcon}>{'\u{1F5A5}'}</span>
              RUNS VIA DCC
            </div>
            <div style={ug.fucDccBadge}>
              <span style={ug.fucDccIcon}>{'\u{1F310}'}</span>
              RUNS IN YOUR BROWSER
            </div>
          </>
        ) : (
          <div style={ug.fucDccBadge}>
            <span style={ug.fucDccIcon}>{'\u{1F5A5}'}</span>
            RUNS VIA DCC
          </div>
        )}
        <div style={ug.fucFreeNote}>Free with your VISINVOKE account &middot; full utility, not a trial</div>
        {paidAddon && (
          <a href={paidAddon.href} style={ug.fucAddon}>
            <span style={ug.fucAddonDot}>{'\u2601'}</span>
            <span style={ug.fucAddonText}><strong>{paidAddon.label || 'Optional paid AI Rebuild'}</strong> &middot; {paidAddon.note}</span>
            <span style={ug.fucAddonGo}>{'\u2192'}</span>
          </a>
        )}
      </div>
      {detailKey ? <MoreInfoPill productKey={detailKey} accent={a} style={{ marginTop: 4 }} /> : null}
    </div>
  );
}

// Capability pill shown in the Knowledge Centre header row.
function KcCapabilityPill({ icon, title, sub, accent }) {
  const a = accent || '#34d399';
  return (
    <div style={ug.kcPill}>
      <div style={{ ...ug.kcPillIcon, color: a, background: hexA(a, 0.12), border: `1px solid ${hexA(a, 0.35)}` }}>{icon}</div>
      <div>
        <div style={ug.kcPillTitle}>{title}</div>
        <div style={ug.kcPillSub}>{sub}</div>
      </div>
    </div>
  );
}

// Small feature marker in the bottom information bar.
function KcInfoFeature({ icon, title, sub, accent }) {
  const a = accent || '#34d399';
  return (
    <div style={ug.infoFeature}>
      <div style={{ ...ug.infoFeatureIcon, color: a }}>{icon}</div>
      <div>
        <div style={ug.infoFeatureTitle}>{title}</div>
        <div style={ug.infoFeatureSub}>{sub}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCC CLOUD SERVICES + WORKFORCE EXTENSIONS presentation section.
// Full-content-width block that sits immediately below the SCC / ACC cards.
// Built entirely from the dashboard's established visual language (same card
// gradient, radii, borders, typography, feature-dot lists and section-badge
// heading pattern used elsewhere on this page). Presentation only — no purchase
// buttons or routes are introduced (none exist to wire, and the approved
// reference shows none). Fully responsive via CSS grid auto-fit; no fixed
// widths that could force horizontal scrolling.
// ---------------------------------------------------------------------------

// Single SCC Cloud tier card. The recommended tier (Guardian) receives a
// restrained gold border + glow and a gold eyebrow, matching the reference.
function SccCloudTierCard({ tier }) {
  const recommended = !!tier.recommended;
  const accent = recommended ? SCC_CLOUD_GOLD : SCC_CLOUD_ACCENT;
  const eyebrowColor = recommended ? SCC_CLOUD_GOLD : 'rgba(148,163,184,0.85)';

  const cardStyle = {
    ...ug.csCard,
    border: '1px solid ' + hexA(accent, recommended ? 0.55 : 0.28),
    boxShadow: recommended
      ? '0 10px 44px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ' + hexA(SCC_CLOUD_GOLD, 0.28) + ', 0 0 30px ' + hexA(SCC_CLOUD_GOLD, 0.20)
      : '0 10px 44px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ' + hexA(SCC_CLOUD_ACCENT, 0.10) + ', 0 0 22px ' + hexA(SCC_CLOUD_ACCENT, 0.10),
  };

  return (
    <div className="util-card-hover util-reveal" style={cardStyle}>
      <span style={{ ...ug.csEyebrow, color: eyebrowColor }}>{tier.eyebrow}</span>
      <div style={ug.csTitle}>{tier.title}</div>
      <div style={ug.csPriceRow}>
        <span style={ug.csPrice}>{tier.price}</span>
        <span style={ug.csBilling}>{tier.billing}</span>
      </div>
      <ul style={ug.csFeatureList}>
        {tier.features.map((f, i) => (
          <li key={i} style={ug.csFeatureItem}>
            <span style={{ ...ug.csDot, background: accent }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Compact Workforce Extension card. Emphasises the total-staff figure with the
// dashboard blue accent.
function WorkforceExtensionCard({ ext }) {
  return (
    <div className="util-card-hover util-reveal" style={ug.wxCard}>
      <div style={ug.wxTitle}>{ext.title}</div>
      <div style={ug.wxPriceRow}>
        <span style={ug.wxPrice}>{ext.price}</span>
        <span style={ug.wxBilling}>{ext.billing}</span>
      </div>
      <div style={ug.wxExpand}>
        <span style={ug.wxStaffAccent}>{ext.totalStaff}</span> allowance
      </div>
    </div>
  );
}

// Full SCC Cloud Services + Workforce Extensions section.
export function SccCloudServicesSection() {
  return (
    <div style={ug.csSection}>
      {/* SCC CLOUD SERVICES */}
      <div style={ug.csHeadingBlock}>
        <h2 style={ug.csHeading}>SCC CLOUD SERVICES <span style={ug.csHeadingMuted}>— OPTIONAL MANAGED PROTECTION</span></h2>
      </div>

      {/* Optional information panel */}
      <div className="util-reveal" style={ug.csInfoPanel}>
        <span style={ug.csInfoIcon}>{'\u{1F6E1}'}</span>
        <p style={ug.csInfoText}>
          <span style={ug.csInfoStrong}>Cloud Services are optional and never required to use SCC.</span>{' '}
          They do not unlock functionality. Keep your own OneDrive, SharePoint, NAS, file server or existing
          backups — these tiers simply add VISINVOKE-managed backup, retention and recovery on top.
        </p>
      </div>

      {/* Three cloud tier cards. Responsive columns handled purely via the
          .cs-grid class (base + media queries) so breakpoints are not overridden
          by an inline grid-template-columns. */}
      <div className="cs-grid">
        {SCC_CLOUD_TIERS.map((tier, i) => (
          <SccCloudTierCard key={i} tier={tier} />
        ))}
      </div>

      {/* WORKFORCE EXTENSIONS */}
      <div style={{ ...ug.csHeadingBlock, marginTop: 8 }}>
        <h2 style={ug.csHeading}>WORKFORCE EXTENSIONS <span style={ug.csHeadingMuted}>— EXPAND BEYOND 10 STAFF</span></h2>
      </div>

      <div className="wx-grid">
        {WORKFORCE_EXTENSIONS.map((ext, i) => (
          <WorkforceExtensionCard key={i} ext={ext} />
        ))}
      </div>

      <p style={ug.csFootnote}>
        Designed for businesses from solo operators through to growing 50-person service teams.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VISINVOKE TECHNICIAN — gold application/discovery box (Utilities entry point).
// One of three entry points into the SAME canonical Technician Application
// (/dashboard/technician/apply). State-aware CTA driven by the authoritative
// backend application state (/v1/technician-application/me). The frontend never
// decides approval — it only reflects state and routes into the application.
// ---------------------------------------------------------------------------
function TechnicianApplyBox() {
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    API.get('/v1/technician-application/me')
      .then((r) => { if (alive) setApp(r.data && r.data.application ? r.data.application : null); })
      .catch(() => { if (alive) setApp(null); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const status = app ? app.status : null;
  // Map authoritative status -> box copy + CTA. Approved (active) points to DCC.
  let cta = 'Apply for Technician Access';
  let action = () => navigate('/dashboard/technician/apply');
  let sub = 'Professional tools for verified technicians and technical service businesses. Access is granted after a manual VISINVOKE review.';
  let stateLabel = '';
  if (status === 'draft') { cta = 'Continue Application'; stateLabel = 'Application in progress'; }
  else if (status === 'pending' || status === 'under_review' || status === 'approved' || status === 'expired_review') {
    cta = 'View Application'; stateLabel = 'Application under review';
    sub = 'Your VISINVOKE Technician application is being reviewed. You’ll gain access if it’s approved.';
  }
  else if (status === 'more_info_required') { cta = 'Update Application'; stateLabel = 'More information required'; sub = 'VISINVOKE has requested additional details before your application can proceed.'; }
  else if (status === 'active') {
    cta = 'Open Desktop Command Centre'; stateLabel = 'Technician access approved';
    sub = 'Your VISINVOKE Technician access is active. Get or launch VISINVOKE Technician from the Desktop Command Centre.';
    action = () => navigate('/desktop-command-center');
  }
  else if (status === 'rejected') { cta = 'View Application'; stateLabel = 'Application not approved'; }
  else if (['suspended', 'paused', 'banned', 'cancelled'].includes(status)) { cta = 'View Application'; stateLabel = 'Access restricted'; }

  if (!loaded) return null; // avoid a flash before authoritative state is known

  return (
    <div className="util-card-hover util-reveal" style={tb.box}>
      <div style={tb.topLine} />
      <span style={tb.badge}><span style={tb.dot} />VISINVOKE TECHNICIAN{stateLabel ? ` · ${stateLabel}` : ''}</span>
      <h3 style={tb.title}>Are you a technician or technical service business?</h3>
      <p style={tb.lead}>{sub}</p>
      <button className="util-btn-hover" style={tb.cta} onClick={action}>{cta}</button>
    </div>
  );
}

const tb = {
  box: { position: 'relative', background: 'linear-gradient(160deg,#1c1608,#241a06)', border: '1px solid #D4AF37', borderRadius: 18, padding: '26px 28px', overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,0,0,0.35)' },
  topLine: { position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg,#D4AF37,#f5d772,#b8942b)' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 8, color: '#0a0a0a', background: '#D4AF37', borderRadius: 999, padding: '5px 13px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em' },
  dot: { width: 7, height: 7, borderRadius: 999, background: '#0a0a0a', display: 'inline-block' },
  title: { color: '#fff', fontSize: 20, fontWeight: 800, margin: '14px 0 8px' },
  lead: { color: '#e7d9b0', fontSize: 14, lineHeight: 1.6, margin: '0 0 18px', maxWidth: 620 },
  cta: { background: 'linear-gradient(135deg,#D4AF37,#b8942b)', color: '#0a0a0a', border: 'none', borderRadius: 10, padding: '12px 26px', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' },
};

export default function UtilitiesPage() {
  const navigate = useNavigate();

  // Smooth-scroll to an in-page section, respecting prefers-reduced-motion.
  // scroll-margin-top on each section (below) clears the sticky header.
  const scrollToSection = (id) => {
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (!el) { return; }
    const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'util-motion-css';
    if (!document.getElementById('util-motion-css')) document.head.appendChild(style);
    style.textContent = UTIL_MOTION_CSS;

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('util-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.util-reveal').forEach(el => observer.observe(el));
    return () => { observer.disconnect(); };
  }, []);

  return (
    <div style={ug.page}>
      <ProductNav />

      {/* Nav bar */}

      {/* Title */}
      <div className="vi-desktop-wide" style={ug.heroWrap}>
        <div style={ug.heroTitleRow}>
          <h1 style={ug.heroTitle}><span style={ug.heroTitleWhite}>VISINVOKE</span> Utilities</h1>
        </div>
        <div style={ug.heroSubtitle}>Operational tools designed for real-world business and creator workflows</div>
      </div>

      {/* Section navigation pills */}
      <nav className="vi-desktop-wide" style={ug.sectionNav} aria-label="Utilities sections">
        {UTIL_SECTION_LINKS.map(link => (
          <a
            key={link.id}
            href={'#' + link.id}
            style={ug.sectionPill}
            onClick={e => { e.preventDefault(); scrollToSection(link.id); }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 30px rgba(47,125,255,1), 0 0 62px rgba(47,125,255,0.6), inset 0 0 28px rgba(47,125,255,0.4), inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 26px rgba(0,0,0,0.55)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 20px rgba(47,125,255,0.8), 0 0 46px rgba(47,125,255,0.45), inset 0 0 24px rgba(47,125,255,0.28), inset 0 1px 0 rgba(255,255,255,0.28), 0 10px 26px rgba(0,0,0,0.55)'; e.currentTarget.style.transform = 'none'; }}
          >
            {link.label}
          </a>
        ))}
      </nav>

      {/* Cards */}
      <div className="vi-desktop-wide" style={ug.cardsWrap}>

        {/* ======================= PAID VISINVOKE UTILITIES
            The full product presentations for Service Command Centre, Website
            Capture Suite and Content Planner now live on the dedicated Paid
            Utilities catalogue page (/dashboard/utilities/paid), so this landing
            page stays a short, scalable hub of collections. This is the primary
            collection card: full width and the most prominent card on the page. */}
        <div id="paid-utilities" style={{ ...ug.ucSection, scrollMarginTop: UTIL_ANCHOR_OFFSET }}>
          <div className="util-card-hover util-reveal" style={ug.ucCardPaid}>
            <div style={ug.ucTopLinePaid} />
            <div className="pu-hero">
              {/* Left: identity + purpose */}
              <div style={ug.ucPaidMain}>
                <div style={ug.ucCardHead}>
                  <span style={ug.ucPaidIcon}>{'\u{1F48E}'}</span>
                  <div>
                    <div style={{ ...ug.ucCardEyebrow, color: '#78ADFF' }}>VISINVOKE COMMERCIAL PRODUCTS</div>
                    <h3 style={ug.ucPaidTitle}>Paid VISINVOKE Utilities</h3>
                  </div>
                </div>
                <p style={ug.ucPaidLead}>Professional VISINVOKE utilities for business, productivity and specialist workflows.</p>
                <p style={ug.ucCardBody}>
                  Complete VISINVOKE products, each with its own subscription and each part of the same VISINVOKE
                  ecosystem as your account and the Desktop Command Center. Browse the catalogue for what each product
                  does, what it costs and how it is accessed.
                </p>
                <div style={ug.ucChips}>
                  {['Business', 'Cloud', 'Productivity', 'Professional'].map((c, i) => (
                    <span key={i} style={ug.ucChip}>{c}</span>
                  ))}
                </div>
              </div>

              {/* Right: what is in the catalogue, highest price first */}
              <div className="uc-paid-grid" style={ug.ucPaidList}>
                <div style={ug.ucPaidListLabel}>IN THIS CATALOGUE</div>
                {PAID_UTILITIES.map((p) => (
                  <article key={p.key} className="uc-paid-card" style={ug.ucPaidRow}>
                    <button
                      type="button"
                      style={ug.ucPaidRowMain}
                      onClick={() => navigate('/dashboard/utilities/paid#' + p.key)}
                      aria-label={`View ${p.title} in the paid utilities catalogue`}
                    >
                      {/* ROW 1 - title, own row, one line at desktop */}
                      <span className="uc-paid-name" style={ug.ucPaidRowName}>{p.title.replace('VISINVOKE ', '')}</span>
                      {/* ROW 2 - code / short label */}
                      <span style={ug.ucPaidTypeRow}>
                        <span style={{ ...ug.ucPaidCodeChip, ...glassSurface(p.accent || '#4D98FF', p.accent || '#4D98FF', p.accent || '#4D98FF') }}>{p.icon}</span>
                        <span style={ug.ucPaidTypeText}>Paid VISINVOKE Utility</span>
                      </span>
                      {/* ROW 3 - price, own row */}
                      <span style={ug.ucPaidPriceRow}>
                        <span style={ug.ucPaidRowPrice}>{p.price}</span>
                        {p.billing ? <span style={ug.ucPaidBilling}>{p.billing}</span> : null}
                      </span>
                      {/* ROW 4 - concise description */}
                      {p.body ? <span className="uc-paid-desc" style={ug.ucPaidDesc}>{p.body}</span> : null}
                      {/* ROW 5 - status / due, bottom-aligned */}
                      {(p.statusLabel || p.dueLabel) ? (
                        <span style={ug.ucPaidStatusRow}>
                          {p.statusLabel ? <AvailabilityBadge status={p.statusLabel} size="sm" /> : null}
                          {p.dueLabel ? <span style={ug.ucPaidDue}>{p.dueLabel}</span> : null}
                        </span>
                      ) : null}
                    </button>
                    <MoreInfoPill
                      productKey={p.detailKey || p.key}
                      route={p.moreInfoRoute}
                      accent={p.accent || '#4D98FF'}
                      style={ug.ucPaidMoreInfo}
                    />
                  </article>
                ))}
                <button
                  className="util-btn-hover"
                  style={{ ...ug.ucBtn, ...ug.ucPaidBtn, ...glassSurface('#123A8A', '#2F7DFF', '#63A6FF') }}
                  onClick={() => navigate('/dashboard/utilities/paid')}
                >
                  View Paid Utilities
                </button>
              </div>
            </div>
          </div>
        </div>

        <div id="utility-collections" style={{ ...ug.ucSection, scrollMarginTop: UTIL_ANCHOR_OFFSET }}>
          <div style={ug.ucHeadingWrap}>
            <h2 style={ug.ucHeading}>VISINVOKE UTILITY COLLECTIONS</h2>
            <p style={ug.ucSub}>Two dedicated collections. Browse the full catalogue on each collection&rsquo;s own page &mdash; utilities run in the VISINVOKE Desktop Command Center, not in the browser.</p>
          </div>

          <div className="uc-grid" style={ug.ucGrid}>
            {/* FREE VISINVOKE UTILITIES */}
            <div className="util-card-hover util-reveal" style={ug.ucCardFree}>
              <div style={ug.ucTopLineFree} />
              <div style={ug.ucCardHead}>
                <span style={ug.ucCardIcon}>{'\u{1F9F0}'}</span>
                <div>
                  <div style={{ ...ug.ucCardEyebrow, color: '#78ADFF' }}>INCLUDED WITH EVERY VISINVOKE ACCOUNT</div>
                  <h3 style={ug.ucCardTitle}>Free VISINVOKE Utilities</h3>
                </div>
              </div>
              <p style={ug.ucCardLead}>Practical Windows and business utilities included with every VISINVOKE account.</p>
              <p style={ug.ucCardBody}>
                A growing collection of desktop utilities for backups, documents, images, files and everyday business
                work. Available to every VISINVOKE member and launched through the VISINVOKE Desktop Command Center.
              </p>
              <div style={ug.ucChips}>
                {['Backups', 'PDF', 'Images', 'File Conversion', 'USB'].map((c, i) => (
                  <span key={i} style={ug.ucChip}>{c}</span>
                ))}
              </div>
              <div style={ug.ucFooter}>
                <span style={ug.ucCount}>{FREE_UTILITIES.length} utilities</span>
                <button
                  className="util-btn-hover"
                  style={{ ...ug.ucBtn, ...glassSurface('#123A8A', '#2F7DFF', '#63A6FF') }}
                  onClick={() => navigate('/dashboard/utilities/free')}
                >
                  View Free Utilities
                </button>
              </div>
            </div>

            {/* VISINVOKE TECH UTILITIES */}
            <div className="util-card-hover util-reveal" style={ug.ucCardTech}>
              <div style={ug.ucTopLineTech} />
              <span style={ug.ucTechBadge}><span style={ug.ucTechDot} />TECHNICIAN ACCESS</span>
              <div style={ug.ucCardHead}>
                <span style={ug.ucCardIcon}>{'\u{1F6E0}'}</span>
                <div>
                  <div style={{ ...ug.ucCardEyebrow, color: '#fcd34d' }}>FOR APPROVED VISINVOKE TECHNICIANS</div>
                  <h3 style={ug.ucCardTitle}>VISINVOKE Tech Utilities</h3>
                </div>
              </div>
              <p style={ug.ucCardLead}>Diagnostics, repair resources, registry tools, Windows knowledge and specialist utilities for approved VISINVOKE technicians.</p>
              <p style={ug.ucCardBody}>
                Everyone can explore the technician catalogue to see what approved technicians receive. Actual use of
                technician tools, registry resources and specialist utilities requires manually approved VISINVOKE
                Technician Access and runs through the Desktop Command Center.
              </p>
              <div style={ug.ucChips}>
                {['Diagnostics', 'Repair Tools', 'Registry', 'Scripts', 'Packages', 'Windows Knowledge'].map((c, i) => (
                  <span key={i} style={ug.ucChipTech}>{c}</span>
                ))}
              </div>
              <div style={ug.ucFooter}>
                <a
                  style={ug.ucSecondaryLink}
                  href="/dashboard/utilities/registry"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/utilities/registry'); }}
                >
                  Windows Knowledge Centre &amp; Registry {'→'}
                </a>
                <button
                  className="util-btn-hover"
                  style={{ ...ug.ucBtn, ...glassSurface('#A66D05', '#f59e0b', '#FBBF24') }}
                  onClick={() => navigate('/dashboard/utilities/tech-hub')}
                >
                  Explore Tech Utilities
                </button>
              </div>
            </div>
          </div>

          {/* VISINVOKE TECHNICIAN — application / discovery (gold, state-aware).
              Rendered beneath the two collection cards so FREE VISINVOKE
              UTILITIES and VISINVOKE TECH UTILITIES stay side by side as a
              balanced desktop pair. */}
          <div style={ug.ucTechnicianWrap}>
            <TechnicianApplyBox />
          </div>
        </div>

      </div>
    </div>
  );
}

const ug = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(47,125,255,0.09) 0%, rgba(0,0,0,0) 44%), radial-gradient(1000px 720px at 100% 112%, rgba(47,125,255,0.05) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  techUtilSection: {
    marginTop: 40,
  },
  techUtilCard: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 18,
    padding: '30px 32px',
    background: 'linear-gradient(160deg, #10233a 0%, #0f1a2c 100%)',
    border: '1px solid ' + hexA('#38bdf8', 0.35),
    boxShadow: '0 10px 34px ' + hexA('#38bdf8', 0.18) + ', 0 2px 10px rgba(0,0,0,0.4)',
  },
  techUtilSheen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 90,
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(56,189,248,0.12) 0%, transparent 100%)',
  },
  techUtilHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    zIndex: 1,
  },
  techUtilIcon: {
    fontSize: 40,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },
  techUtilTitle: {
    fontSize: 26,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.4px',
    margin: '0 0 4px',
    textAlign: 'center',
  },
  techUtilSubtitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#7dd3fc',
    margin: 0,
    textAlign: 'center',
  },
  techUtilBody: {
    position: 'relative',
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: '18px 0 0',
    maxWidth: 900,
    zIndex: 1,
    textAlign: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  techUtilChips: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    margin: '18px 0 0',
    zIndex: 1,
  },
  techUtilChip: {
    fontSize: 12,
    fontWeight: 700,
    color: '#cbd5e1',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid ' + hexA('#ffffff', 0.14),
    borderRadius: 20,
    padding: '5px 12px',
  },
  techUtilBtn: {
    position: 'relative',
    display: 'block',
    margin: '22px auto 0',
    borderRadius: 10,
    padding: '12px 26px',
    fontSize: 15,
    fontWeight: 800,
    color: '#04121f',
    background: '#38bdf8',
    border: 'none',
    cursor: 'pointer',
    zIndex: 1,
  },

  hero: {
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(160deg, #0d1117 0%, #111827 50%, #0d1117 100%)',
    borderBottom: 'none',
    padding: '12px 0 10px',
  },

  heroGlow: {
    position: 'absolute',
    top: -40,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 600,
    height: 200,
    borderRadius: '50%',
    background: 'radial-gradient(ellipse, rgba(47,125,255,0.16) 0%, transparent 70%)',
    pointerEvents: 'none',
  },

  heroInner: {
    position: 'relative',
    maxWidth: 820,
    margin: '0 auto',
    padding: '0 40px',
    textAlign: 'center',
    zIndex: 1,
    boxSizing: 'border-box',
    width: '100%',
  },

  heroWrap: {
    maxWidth: 1400,
    margin: '0 auto',
    textAlign: 'center',
    padding: '24px 40px 18px',
    boxSizing: 'border-box',
    width: '100%',
  },

  heroTitleRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  heroPillRow: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  heroTitle: {
    fontSize: 52,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.1,
    margin: 0,
    letterSpacing: '-1px',
  },

  heroTitleWhite: {
    color: '#ffffff',
  },

  heroSubtitle: {
    fontSize: 18,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.5,
    marginTop: 12,
  },

  sectionNav: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '4px 40px 2px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'center',
  },

  sectionPill: {
    ...glassSurface('#123A8A', '#2F7DFF', '#63A6FF'),
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 20,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
  },

  cardsWrap: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '14px 40px 72px',
    display: 'flex',
    flexDirection: 'column',
    gap: 36,
    boxSizing: 'border-box',
    width: '100%',
  },

  // -------------------------------------------------------------------------
  // VISINVOKE UTILITY COLLECTIONS — two large destination cards that replace
  // the former on-page Free Utilities / Windows Knowledge Centre / Tech
  // Utilities catalogues. Built from the dashboard's card language. Grid
  // columns live in the .uc-grid class, not inline, so breakpoints hold.
  // -------------------------------------------------------------------------
  ucSection: {
    boxSizing: 'border-box',
    width: '100%',
  },
  ucHeadingWrap: {
    textAlign: 'center',
    marginBottom: 22,
  },
  ucHeading: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '0.14em',
    margin: 0,
    lineHeight: 1.2,
  },
  ucSub: {
    fontSize: 15,
    fontWeight: 500,
    color: '#a8b2c2',
    lineHeight: 1.55,
    margin: '10px auto 0',
    maxWidth: 760,
  },
  ucGrid: {
    width: '100%',
    boxSizing: 'border-box',
  },

  // Technician application box sits under the two collection cards at the full
  // content width, so the Free / Tech Utilities pair stays side by side.
  ucTechnicianWrap: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 24,
  },

  // -------------------------------------------------------------------------
  // VISINVOKE CONTENT PLANNER — paid utility section. Mirrors the Website
  // Capture Suite block's structure and typography with the Content Planner
  // accent, so both paid utilities read as one product-card system.
  // -------------------------------------------------------------------------
  cpSection: {
    position: 'relative',
    border: '1px solid ' + hexA('#f472b6', 0.38),
    borderRadius: 18,
    padding: '40px 34px 36px',
    margin: '20px 0',
    background: 'linear-gradient(165deg, #1c1024 0%, #24132e 45%, #150c1c 100%)',
    boxShadow: '0 0 0 1px ' + hexA('#f472b6', 0.12) + ', 0 0 34px ' + hexA('#f472b6', 0.14) + ', 0 10px 46px rgba(0,0,0,0.5)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
  },
  cpHeader: {
    textAlign: 'center',
    minHeight: 128,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  cpHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  cpHeaderIcon: {
    fontSize: 40,
    lineHeight: 1,
    filter: 'drop-shadow(0 0 14px ' + hexA('#f472b6', 0.5) + ')',
  },
  cpHeading: {
    fontSize: 42,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    margin: 0,
    lineHeight: 1.1,
  },
  cpHeadingAccent: {
    color: '#f472b6',
    textShadow: '0 0 18px ' + hexA('#f472b6', 0.45),
  },
  cpSubtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#e2d3e0',
    lineHeight: 1.5,
    margin: '12px 0 0',
  },
  cpProduct: {
    display: 'flex',
    flexDirection: 'column',
    gap: 22,
    background: 'linear-gradient(160deg, rgba(60,28,58,0.6) 0%, rgba(38,20,40,0.6) 100%)',
    border: '1px solid rgba(148,163,184,0.18)',
    borderRadius: 14,
    padding: '26px 26px 28px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  cpProductTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  cpProductNameBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 260,
    flex: 1,
  },
  cpColLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
  cpProductName: {
    fontSize: 28,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    lineHeight: 1.15,
  },
  cpProductRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  cpPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
  },
  cpPrice: {
    fontSize: 28,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  cpBilling: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  cpPriceNote: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.55)',
    whiteSpace: 'nowrap',
  },
  cpBtn: {
    marginTop: 4,
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 10,
    padding: '13px 30px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'not-allowed',
  },
  cpHero: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  cpHeroText: {
    fontSize: 15,
    color: '#e6d8e6',
    lineHeight: 1.7,
    margin: 0,
  },
  cpBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 700,
    color: '#ffffff',
  },
  cpBadgeCheck: {
    fontSize: 13,
    fontWeight: 900,
    flexShrink: 0,
  },
  cpEqualPanel: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px',
    borderRadius: 12,
    background: 'linear-gradient(160deg, rgba(244,114,182,0.10) 0%, rgba(244,114,182,0.04) 100%)',
    border: '1px solid ' + hexA('#f472b6', 0.30),
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  cpEqualIcon: {
    fontSize: 22,
    flexShrink: 0,
    filter: 'drop-shadow(0 0 10px ' + hexA('#f472b6', 0.4) + ')',
  },
  cpEqualText: {
    fontSize: 14,
    color: '#e6d8e6',
    lineHeight: 1.6,
    margin: 0,
  },
  cpEqualStrong: {
    color: '#ffffff',
    fontWeight: 800,
  },
  cpTierCard: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: 'linear-gradient(160deg, rgba(60,28,58,0.65) 0%, rgba(34,18,36,0.65) 100%)',
    borderRadius: 14,
    padding: '26px 26px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
  },
  cpTierEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  cpTierTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    lineHeight: 1.2,
  },
  cpTierPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  cpTierPrice: {
    fontSize: 34,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.6px',
  },
  cpTierBilling: {
    fontSize: 15,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  cpTierCapacity: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    border: '1px solid',
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.04em',
    background: 'rgba(255,255,255,0.06)',
  },
  cpTierCapacityDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  cpTierList: {
    listStyle: 'none',
    margin: '4px 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: 1,
  },
  cpTierItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    color: '#eadaea',
    lineHeight: 1.5,
  },
  cpTierDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  cpFootnote: {
    fontSize: 13,
    color: 'rgba(230,216,230,0.75)',
    lineHeight: 1.6,
    margin: 0,
    textAlign: 'center',
  },
  // -------------------------------------------------------------------------
  // PAID VISINVOKE UTILITIES collection card — the landing page's primary card.
  // Same blue family and card language as the Free Utilities collection card,
  // scaled up (full width, stronger border/glow, larger heading) so it clearly
  // outranks the two collection cards below without looking disconnected.
  // -------------------------------------------------------------------------
  ucCardPaid: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    background: viGlassFill('#123A8A', '#2F7DFF', '#63A6FF'),
    border: viGlassBorder('#63A6FF', 1.5),
    borderRadius: 18,
    padding: '34px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
    boxShadow: viGlassShadow('#123A8A', '#2F7DFF', '#63A6FF', false),
  },
  ucTopLinePaid: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    background: 'linear-gradient(90deg, #2F7DFF, #4D98FF, #174DB8)',
  },
  ucPaidMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },
  ucPaidIcon: {
    fontSize: 40,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 0 14px rgba(47, 125, 255,0.45))',
  },
  ucPaidTitle: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    lineHeight: 1.15,
    margin: '6px 0 0',
  },
  ucPaidLead: {
    fontSize: 17,
    fontWeight: 700,
    color: '#e2e8f0',
    lineHeight: 1.5,
    margin: 0,
  },
  ucPaidList: {
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 14,
    border: '1px solid ' + hexA('#2F7DFF', 0.28),
    background: 'rgba(47, 125, 255,0.06)',
    padding: '20px',
  },
  ucPaidListLabel: {
    gridColumn: '1 / -1',
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.66)',
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
  },
  ucPaidRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    padding: 0,
    borderRadius: 12,
    background: viGlassFill('#334155', '#64748B', '#94A3B8'),
    border: viGlassBorder('#94A3B8', 1.5),
    boxShadow: viGlassShadow('#334155', '#64748B', '#94A3B8', false),
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    color: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'default',
    overflow: 'hidden',
  },
  ucPaidRowMain: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: 9,
    width: '100%',
    minWidth: 0,
    padding: '20px 20px 14px',
    border: 0,
    background: 'transparent',
    color: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  ucPaidMoreInfo: {
    width: 'auto',
    margin: '0 20px 20px',
    padding: '10px 14px',
    fontSize: 10.5,
  },
  ucPaidRowHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  ucPaidRowIcon: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: '0.03em',
    lineHeight: 1,
    flexShrink: 0,
  },
  ucPaidRowName: {
    minWidth: 0,
    fontSize: 18,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.25,
    letterSpacing: '-0.2px',
  },
  ucPaidRowPriceLine: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '4px 10px',
    width: '100%',
  },
  ucPaidRowStatus: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.62)',
    lineHeight: 1.35,
  },
  ucPaidRowType: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.50)',
    lineHeight: 1.35,
  },
  ucPaidTypeRow: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
  ucPaidCodeChip: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 30, height: 22, padding: '0 8px', borderRadius: 7,
    border: '1px solid', fontSize: 12.5, fontWeight: 900, lineHeight: 1, flexShrink: 0,
  },
  ucPaidTypeText: {
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.58)', minWidth: 0,
  },
  ucPaidPriceRow: { display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' },
  ucPaidBilling: { fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.55)' },
  ucPaidDesc: {
    fontSize: 13.5, color: 'rgba(255,255,255,0.74)', lineHeight: 1.5, margin: 0, minHeight: 40,
  },
  ucPaidStatusRow: { marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', paddingTop: 4 },
  ucPaidDue: { fontSize: 11.5, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.06em' },
  ucPaidRowPrice: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.05,
    whiteSpace: 'nowrap',
  },
  ucPaidBtn: {
    gridColumn: '1 / -1',
    marginTop: 6,
    width: '100%',
    fontSize: 15,
    padding: '14px 22px',
  },
  ucCardFree: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: viGlassFill('#123A8A', '#2F7DFF', '#63A6FF'),
    border: viGlassBorder('#63A6FF', 1.5),
    borderRadius: 16,
    padding: '30px 28px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
    boxShadow: viGlassShadow('#123A8A', '#2F7DFF', '#63A6FF', false),
  },
  ucCardTech: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: viGlassFill('#A66D05', '#f59e0b', '#FBBF24'),
    border: viGlassBorder('#FBBF24', 1.5),
    borderRadius: 16,
    padding: '30px 28px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
    boxShadow: viGlassShadow('#A66D05', '#f59e0b', '#FBBF24', false),
  },
  ucTopLineFree: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #2F7DFF, #4D98FF)',
    pointerEvents: 'none',
  },
  ucTopLineTech: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
    pointerEvents: 'none',
  },
  ucTechBadge: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: '#fff',
    background: viGlassFill('#A66D05', '#f59e0b', '#FBBF24'),
    border: viGlassBorder('#FBBF24', 1.5),
    boxShadow: viGlassShadow('#A66D05', '#f59e0b', '#FBBF24', false),
    textShadow: '0 0 10px rgba(251,191,36,0.9), 0 1px 4px rgba(0,0,0,0.6)',
    borderRadius: 100,
    padding: '5px 12px',
    whiteSpace: 'nowrap',
  },
  ucTechDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#fbbf24',
    flexShrink: 0,
  },
  ucCardHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  ucCardIcon: {
    fontSize: 34,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))',
  },
  ucCardEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  ucCardTitle: {
    fontSize: 24,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.4px',
    lineHeight: 1.15,
    margin: 0,
  },
  ucCardLead: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e2e8f0',
    lineHeight: 1.5,
    margin: 0,
  },
  ucCardBody: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.78)',
    lineHeight: 1.62,
    margin: 0,
    flex: 1,
  },
  ucChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  ucChip: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: viGlassFill('#123A8A', '#2F7DFF', '#63A6FF'),
    border: viGlassBorder('#63A6FF', 1.5),
    boxShadow: viGlassShadow('#123A8A', '#2F7DFF', '#63A6FF', false),
    textShadow: '0 0 10px rgba(99,166,255,0.9), 0 1px 4px rgba(0,0,0,0.6)',
    borderRadius: 20,
    padding: '6px 12px',
  },
  ucChipTech: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: viGlassFill('#A66D05', '#f59e0b', '#FBBF24'),
    border: viGlassBorder('#FBBF24', 1.5),
    boxShadow: viGlassShadow('#A66D05', '#f59e0b', '#FBBF24', false),
    textShadow: '0 0 10px rgba(251,191,36,0.9), 0 1px 4px rgba(0,0,0,0.6)',
    borderRadius: 20,
    padding: '6px 12px',
  },
  ucFooter: {
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    flexWrap: 'wrap',
  },
  ucCount: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  ucSecondaryLink: {
    fontSize: 13,
    fontWeight: 700,
    color: '#fcd34d',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  ucBtn: {
    border: 'none',
    borderRadius: 10,
    padding: '13px 24px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  },

  // MAIN PROGRAMS section
  mainProgramsSection: {
    position: 'relative',
    border: '1px solid rgba(47,125,255,0.45)',
    borderRadius: 16,
    padding: '40px 32px 32px',
    boxShadow: '0 0 0 1px rgba(47,125,255,0.16), 0 0 28px rgba(47,125,255,0.18), 0 8px 40px rgba(0,0,0,0.4)',
    background: 'linear-gradient(180deg, rgba(47,125,255,0.04) 0%, rgba(13,17,23,0) 60%)',
    boxSizing: 'border-box',
  },

  sectionBadge: {
    position: 'absolute',
    top: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#0d1117',
    border: '1px solid rgba(47,125,255,0.5)',
    borderRadius: 20,
    padding: '6px 18px',
    color: '#78ADFF',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    boxShadow: '0 0 16px rgba(47,125,255,0.3)',
    whiteSpace: 'nowrap',
  },

  sectionBadgeIcon: {
    fontSize: 14,
  },

  // Windows Knowledge Centre section
  kcSection: {
    border: '1px solid rgba(52,211,153,0.40)',
    borderRadius: 16,
    padding: '36px 32px 32px',
    boxShadow: '0 0 0 1px rgba(52,211,153,0.14), 0 0 28px rgba(52,211,153,0.14), 0 8px 40px rgba(0,0,0,0.4)',
    background: 'linear-gradient(180deg, rgba(52,211,153,0.04) 0%, rgba(13,17,23,0) 55%)',
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
    boxSizing: 'border-box',
  },

  kcIntro: {
    textAlign: 'center',
  },

  kcHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },

  kcBookIcon: {
    fontSize: 44,
    lineHeight: 1,
    color: '#34d399',
    textShadow: '0 0 18px rgba(52,211,153,0.55)',
  },

  kcHeading: {
    fontSize: 42,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    margin: 0,
    lineHeight: 1.1,
  },

  kcHeadingAccent: {
    color: '#34d399',
    textShadow: '0 0 18px rgba(52,211,153,0.4)',
  },

  kcSubtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.5,
    margin: '10px 0 0',
  },

  kcPillRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
  },

  kcPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 13,
    background: 'linear-gradient(160deg, #131f33 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '16px 17px',
    boxShadow: '0 4px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
  },

  kcPillIcon: {
    fontSize: 18,
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  kcPillTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
  },

  kcPillSub: {
    fontSize: 11,
    fontWeight: 500,
    color: '#aab8c9',
    lineHeight: 1.3,
    marginTop: 1,
  },

  kcGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 16,
  },

  // Registry Tweaks heading (above category grid)
  regHeadingBlock: {
    textAlign: 'center',
    marginTop: 2,
  },

  regHeading: {
    fontSize: 24,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: '0 0 6px',
  },

  regHeadingSub: {
    fontSize: 15,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: 820,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  // Full-colour themed card
  tcCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 14,
    padding: '18px 17px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
    transition: 'transform 240ms cubic-bezier(0.22,1,0.36,1), box-shadow 240ms ease, border-color 240ms ease',
  },

  // -------------------------------------------------------------------------
  // Large free-utility catalogue card (five across on desktop). Same colour
  // language as tcCard, with the padding, typography and depth needed to carry
  // real capability information instead of a one-line summary.
  // -------------------------------------------------------------------------
  fucCard: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 16,
    padding: '24px 22px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
    transition: 'transform 240ms cubic-bezier(0.22,1,0.36,1), box-shadow 240ms ease, border-color 240ms ease',
  },

  fucHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    zIndex: 1,
  },

  fucIcon: {
    fontSize: 30,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },

  fucTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.28,
    letterSpacing: '-0.2px',
    textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  },

  fucDesc: {
    position: 'relative',
    fontSize: 14,
    color: 'rgba(255,255,255,0.92)',
    lineHeight: 1.6,
    zIndex: 1,
  },

  fucPointsWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
    flex: 1,
    zIndex: 1,
  },

  fucPointsLabel: {
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
  },

  fucPoints: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  fucPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    fontSize: 13.5,
    color: 'rgba(255,255,255,0.94)',
    lineHeight: 1.45,
  },

  fucPointDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#ffffff',
    flexShrink: 0,
    marginTop: 7,
    boxShadow: '0 0 6px rgba(255,255,255,0.5)',
  },

  fucFooter: {
    position: 'relative',
    marginTop: 'auto',
    paddingTop: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    zIndex: 1,
  },

  fucStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  fucStatusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#ffffff',
    flexShrink: 0,
    boxShadow: '0 0 8px rgba(255,255,255,0.7)',
  },

  fucStatusText: {
    fontSize: 11.5,
    fontWeight: 800,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  fucDccBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 999,
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.38)',
    color: '#ffffff',
    fontSize: 12.5,
    fontWeight: 900,
    letterSpacing: '0.12em',
  },

  fucDccIcon: {
    fontSize: 14,
    lineHeight: 1,
    flexShrink: 0,
  },

  fucAddon: {
    display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '9px 12px',
    borderRadius: 10, textDecoration: 'none',
    background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.4)',
    color: '#d7f7e8', fontSize: 12.5, lineHeight: 1.4,
  },
  fucAddonDot: { fontSize: 15, color: '#34d399', flex: '0 0 auto' },
  fucAddonText: { flex: 1 },
  fucAddonGo: { color: '#34d399', fontWeight: 800, flex: '0 0 auto' },
  fucFreeNote: {
    fontSize: 11.5,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.45,
  },

  tcSheen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 70,
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%)',
  },

  tcHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    zIndex: 1,
  },

  tcIcon: {
    fontSize: 26,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },

  tcTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
    letterSpacing: '-0.2px',
    textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  },

  tcDesc: {
    position: 'relative',
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.55,
    flex: 1,
    zIndex: 1,
  },

  tcFooter: {
    position: 'relative',
    marginTop: 'auto',
    paddingTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
    zIndex: 1,
  },

  tcFreeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 9,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  tcFreeDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#ffffff',
    flexShrink: 0,
    boxShadow: '0 0 6px rgba(255,255,255,0.6)',
  },

  tcStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },

  tcStatusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },

  tcStatusText: {
    fontSize: 11,
    fontWeight: 800,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  tcBtn: {
    borderRadius: 9,
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 700,
    width: '100%',
    transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease, transform 160ms ease',
  },

  // Embedded category card
  embCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    borderRadius: 12,
    padding: '16px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    textAlign: 'left',
    transition: 'transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms ease, border-color 220ms ease',
  },

  embHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  embIcon: {
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
    transition: 'filter 200ms ease',
  },

  embTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
    letterSpacing: '-0.2px',
  },

  embDesc: {
    fontSize: 12,
    color: '#cbd5e1',
    lineHeight: 1.5,
    flex: 1,
  },

  embFooter: {
    marginTop: 'auto',
    paddingTop: 10,
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  embStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },

  embStatusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },

  embStatusText: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  embBtn: {
    background: 'rgba(255,255,255,0.05)',
    color: '#e2e8f0',
    border: '1px solid',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    width: '100%',
    transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease',
  },

  // Information bar
  infoBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '20px 24px',
    boxSizing: 'border-box',
  },

  infoBarLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    flex: 1,
    minWidth: 280,
  },

  infoBarIcon: {
    fontSize: 20,
    color: '#3b82f6',
    flexShrink: 0,
    marginTop: 2,
  },

  infoBarTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#ffffff',
    fontStyle: 'italic',
    marginBottom: 4,
  },

  infoBarText: {
    fontSize: 13,
    color: '#cbd5e1',
    lineHeight: 1.6,
    maxWidth: 560,
  },

  infoBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 26,
    flexWrap: 'wrap',
  },

  infoFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
  },

  infoFeatureIcon: {
    fontSize: 18,
    flexShrink: 0,
  },

  infoFeatureTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.2,
  },

  infoFeatureSub: {
    fontSize: 12,
    color: '#aab8c9',
    lineHeight: 1.2,
  },

  // Layout lives in the .mp-row class (flex row + established card width), not
  // inline, so the card width and breakpoints are never overridden here.
  activeRow: {
    width: '100%',
    boxSizing: 'border-box',
  },

  activeCard: {
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    border: '1px solid rgba(99,102,241,0.30)',
    borderRadius: 14,
    padding: '30px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 10px 44px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(99,102,241,0.10), 0 0 26px rgba(99,102,241,0.10)',
    textAlign: 'left',
  },

  // -------------------------------------------------------------------------
  // SCC CLOUD SERVICES + WORKFORCE EXTENSIONS
  // Reuses the dashboard's card gradient, radii, feature-dot lists and section
  // heading conventions. Grid columns live in UTIL_MOTION_CSS (.cs-grid /
  // .wx-grid), not here, so responsive breakpoints are respected.
  // -------------------------------------------------------------------------
  csSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    boxSizing: 'border-box',
    width: '100%',
  },
  csHeadingBlock: {
    textAlign: 'center',
  },
  csHeading: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#ffffff',
    margin: 0,
    lineHeight: 1.4,
  },
  csHeadingMuted: {
    color: 'rgba(148,163,184,0.9)',
    fontWeight: 700,
  },
  csInfoPanel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    width: '100%',
    boxSizing: 'border-box',
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid ' + hexA('#38bdf8', 0.28),
    borderRadius: 14,
    padding: '20px 24px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  csInfoIcon: {
    fontSize: 22,
    lineHeight: 1.4,
    flexShrink: 0,
    filter: 'drop-shadow(0 0 10px rgba(56,189,248,0.45))',
  },
  csInfoText: {
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.62,
    color: 'rgba(255,255,255,0.82)',
  },
  csInfoStrong: {
    color: '#ffffff',
    fontWeight: 800,
  },
  csCard: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    borderRadius: 14,
    padding: '24px 24px 26px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
  },
  csEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    lineHeight: 1.3,
  },
  csTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.4px',
    lineHeight: 1.15,
  },
  csPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 2,
    marginBottom: 4,
  },
  csPrice: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  csBilling: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.55)',
  },
  csFeatureList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
    flex: 1,
  },
  csFeatureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 13.5,
    lineHeight: 1.4,
    color: 'rgba(255,255,255,0.82)',
  },
  csDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 6,
  },
  csFootnote: {
    margin: '4px 0 0',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#e2e8f0',
  },

  // Workforce Extension cards (more compact than the Cloud tier cards).
  wxCard: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    height: '100%',
    boxSizing: 'border-box',
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    border: '1px solid ' + hexA('#38bdf8', 0.22),
    borderRadius: 14,
    padding: '22px 22px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ' + hexA('#38bdf8', 0.08),
  },
  wxTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.2px',
    lineHeight: 1.2,
  },
  wxPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 6,
    margin: '2px 0',
  },
  wxPrice: {
    fontSize: 26,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  wxBilling: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.55)',
  },
  wxExpand: {
    fontSize: 13.5,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.72)',
  },
  wxStaffAccent: {
    color: '#38bdf8',
    fontWeight: 800,
  },

  // Free utilities section
  freeSection: {
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },

  // Dedicated introduction container (own full-width panel)
  freeIntro: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: '28px 30px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.04)',
    textAlign: 'center',
  },

  freeIntroTitle: {
    fontSize: 32,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.6px',
    margin: '0 0 10px',
    lineHeight: 1.1,
  },

  freeIntroSubtitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#e2e8f0',
    lineHeight: 1.45,
    margin: '0 0 14px',
    maxWidth: 880,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  freeIntroBody: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 880,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  freeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 16,
  },

  freeCard: {
    background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 40%, #1d4ed8 75%, #3b82f6 100%)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 11,
    padding: '22px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    textAlign: 'left',
  },

  freeCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },

  freeCardIcon: {
    fontSize: 24,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },

  freeCardTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.3,
    marginBottom: 3,
  },

  freeCardDesc: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 1.4,
  },

  freeCardFooter: {
    marginTop: 'auto',
    paddingTop: 10,
    borderTop: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  freeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 10,
    fontWeight: 800,
    color: '#4ade80',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  freeBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#4ade80',
    flexShrink: 0,
    boxShadow: '0 0 6px #4ade8088',
  },

  freeCardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },

  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    border: '1px solid',
    borderRadius: 6,
    padding: '3px 8px',
    flexShrink: 0,
  },

  freeCardLaunchBtn: {
    background: '#ffffff',
    color: '#0f172a',
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    whiteSpace: 'nowrap',
  },

  freeCardDisabledBtn: {
    background: 'rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.35)',
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'not-allowed',
    whiteSpace: 'nowrap',
  },

  // Shared
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },

  cardIcon: {
    fontSize: 28,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },

  cardIconSm: {
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },

  activeCardTitle: {
    fontWeight: 800,
    fontSize: 18,
    color: '#f1f5f9',
    lineHeight: 1.3,
    marginBottom: 4,
  },

  activeCardSubtitle: {
    fontSize: 13,
    fontWeight: 500,
    color: '#94a3b8',
    lineHeight: 1.4,
  },

  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: '#e2e8f0',
    lineHeight: 1.3,
    marginBottom: 4,
  },

  cardStatus: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  cardBody: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.7,
    margin: 0,
    whiteSpace: 'pre-line',
  },

  featureList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: 1,
  },

  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
  },

  featureDotActive: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.7)',
    flexShrink: 0,
  },

  featureDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.5)',
    flexShrink: 0,
  },

  cardNote: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
    fontStyle: 'italic',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    paddingTop: 10,
    marginTop: 4,
  },

  activeCardFooter: {
    marginTop: 'auto',
    paddingTop: 8,
    display: 'flex',
    justifyContent: 'center',
  },

  launchBtn: {
    background: '#ffffff',
    color: '#0f172a',
    border: 'none',
    borderRadius: 10,
    padding: '13px 28px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: 300,
  },

  comingSoonBtn: {
    background: '#ffffff',
    color: '#0f172a',
    border: 'none',
    borderRadius: 10,
    padding: '13px 28px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'not-allowed',
    width: '100%',
    maxWidth: 300,
    opacity: 0.65,
  },

  // Paid operational product cards (Admin CC / Service CC)
  ppCard: {
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    borderRadius: 14,
    padding: '30px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
  },
  ppTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  ppEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
  ppStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 20,
    border: '1px solid',
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  ppStatusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  // Left column of the wide main-product card: identity, pricing, description
  // and the action button, in the card's existing vertical rhythm.
  ppIdentityCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },
  ppNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  ppIcon: {
    fontSize: 30,
    lineHeight: 1,
    flexShrink: 0,
  },
  ppName: {
    fontSize: 26,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.4px',
    lineHeight: 1.1,
  },
  ppPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
  },
  ppPrice: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  ppBilling: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  ppDesc: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.6,
    margin: 0,
  },
  // Capability column of the wide main-product card. Centred in its column so
  // the two columns read as a balanced pair instead of leaving the whitespace
  // pooled under the list.
  ppFeatureGrid: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '14px 18px',
    alignSelf: 'center',
    flex: 1,
  },
  ppFeatureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    fontSize: 13,
    color: 'rgba(255,255,255,0.82)',
  },
  ppDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  ppFooter: {
    marginTop: 6,
  },
  ppBtn: {
    border: 'none',
    borderRadius: 10,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  },
  ppBtnDisabled: {
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'not-allowed',
    width: '100%',
  },

  // Full-width Website Capture Suite card (spans both MAIN PROGRAMS columns).
  wcsCard: {
    gridColumn: '1 / -1',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
    marginTop: 20,
    background: 'linear-gradient(160deg, #14223c 0%, #111c30 45%, #0e1626 100%)',
    borderRadius: 14,
    padding: '26px 28px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
  },
  wcsHeadRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    flexWrap: 'wrap',
  },
  wcsHeadLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 260,
    flex: 1,
  },
  wcsTagline: {
    fontSize: 14,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.72)',
    lineHeight: 1.4,
  },
  wcsPriceBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  wcsPriceNote: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.55)',
    whiteSpace: 'nowrap',
  },
  wcsBodyGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 0.85fr) 2.4fr',
    gap: '20px 36px',
    alignItems: 'stretch',
  },
  wcsLeftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
  },
  wcsRightCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0,
  },
  wcsColLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    height: 16,
    lineHeight: '16px',
  },
  wcsColText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.6,
    margin: 0,
  },
  wcsPointsGrid: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridAutoRows: '38px',
    columnGap: 20,
    alignContent: 'start',
  },
  wcsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    fontSize: 13,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.3,
    paddingTop: 2,
    boxSizing: 'border-box',
    minWidth: 0,
  },
  wcsColBtnWrap: {
    marginTop: 'auto',
    paddingTop: 18,
    display: 'flex',
  },
  wcsBtn: {
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'not-allowed',
    width: '100%',
    maxWidth: 300,
  },

  // ---- Website Intelligence section (deep navy / slate blue / electric cyan) ----
  wiSection: {
    position: 'relative',
    border: '1px solid rgba(34,211,238,0.38)',
    borderRadius: 18,
    padding: '40px 34px 36px',
    margin: '20px 0',
    background: 'linear-gradient(165deg, #0b1830 0%, #0e2038 45%, #0a1526 100%)',
    boxShadow: '0 0 0 1px rgba(34,211,238,0.12), 0 0 34px rgba(34,211,238,0.14), 0 10px 46px rgba(0,0,0,0.5)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
  },
  wiHeader: {
    textAlign: 'center',
    minHeight: 128,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  wiHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  wiHeaderIcon: {
    fontSize: 40,
    lineHeight: 1,
    filter: 'drop-shadow(0 0 14px rgba(34,211,238,0.5))',
  },
  wiHeading: {
    fontSize: 42,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    margin: 0,
    lineHeight: 1.1,
  },
  wiHeadingAccent: {
    color: '#22d3ee',
    textShadow: '0 0 18px rgba(34,211,238,0.45)',
  },
  wiSubtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.5,
    margin: '12px 0 0',
  },
  wiProduct: {
    display: 'flex',
    flexDirection: 'column',
    gap: 22,
    background: 'linear-gradient(160deg, rgba(20,40,68,0.6) 0%, rgba(14,28,48,0.6) 100%)',
    border: '1px solid rgba(148,163,184,0.18)',
    borderRadius: 14,
    padding: '26px 26px 28px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  wiProductTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  wiProductNameBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 260,
    flex: 1,
  },
  wiProductName: {
    fontSize: 28,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
    lineHeight: 1.15,
  },
  wiProductRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  wiPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
  },
  wiPrice: {
    fontSize: 28,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  wiBilling: {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
  },
  wiPriceNote: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.55)',
    whiteSpace: 'nowrap',
  },
  wiBtn: {
    marginTop: 4,
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 10,
    padding: '13px 30px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'not-allowed',
    whiteSpace: 'nowrap',
  },
  wiHero: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    borderTop: '1px solid rgba(148,163,184,0.14)',
    paddingTop: 20,
  },
  wiHeroText: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.86)',
    lineHeight: 1.7,
    margin: 0,
    maxWidth: 1000,
  },
  wiBadges: {
    gap: 10,
  },
  wiBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    fontSize: 12.5,
    fontWeight: 700,
    color: '#e2e8f0',
    background: 'rgba(34,211,238,0.08)',
    border: '1px solid rgba(34,211,238,0.28)',
    borderRadius: 20,
    padding: '6px 13px',
  },
  wiBadgeCheck: {
    fontWeight: 900,
    fontSize: 12,
  },
  wiColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 28,
  },
  wiCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 22,
    alignItems: 'stretch',
  },
  wiCard: {
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(160deg, #0f1e34 0%, #0c1728 100%)',
    border: '1px solid',
    borderRadius: 14,
    padding: '22px 24px 24px',
    boxSizing: 'border-box',
    minWidth: 0,
    height: '100%',
  },
  wiCardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 32,
  },
  wiCardIcon: {
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
  },
  wiCardTitle: {
    fontSize: 17,
    fontWeight: 800,
    letterSpacing: '-0.2px',
  },
  wiCardDivider: {
    height: 1,
    width: '100%',
    margin: '14px 0 16px',
  },
  wiCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  wiCardIntro: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.6,
    margin: 0,
  },
  wiColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0,
  },
  wiColLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  wiCapGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  wiCapMatrix: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px 20px',
  },
  wiCapGroupName: {
    fontSize: 12,
    fontWeight: 800,
    color: '#94a3b8',
    letterSpacing: '0.06em',
  },
  wiRoadmapHeading: {
    fontSize: 14,
    fontWeight: 800,
    color: '#7dd3fc',
    marginBottom: 2,
  },
  wiList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '9px 20px',
  },
  wiListSingle: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
  },
  wiListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    fontSize: 13,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.4,
  },
  wiDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 6,
  },
  wiDotHollow: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    border: '1.5px solid',
    background: 'transparent',
    flexShrink: 0,
    marginTop: 6,
    boxSizing: 'border-box',
  },
};
